<script setup lang="ts">
// ================================
// « Modifier les hypothèses » selon le contrat de l'interface v1.2 §8.2
// (lot 4d, DEV-2311, DEV-2419 ; maquette 2d, états F1 à F4) :
// - bloc « Ce que vous voulez chiffrer » en tête : l'objectif en deux choix,
//   les « Puissances à chiffrer » (seconde instance des champs de structure,
//   préremplies avec la recommandation, distinctes du contrat), la période et
//   la marge de sécurité en quatre crans (absente en mode choix ; 15 %
//   d'office en C5) ; aucun choix de scénario (D2) ;
// - bloc « Contrat actuel » : la consigne sous le titre (A6), formule et
//   puissances avec leur provenance, segment en lecture seule, raccordement
//   avec « non retenue » et le texte D19 quand la valeur est écartée, plages
//   d'heures creuses modifiables avec leur provenance (A8) ;
// - « Réglages d'expert (facultatif) » replié, son résumé écrit le palier
//   appliqué en C5 ; absent en mode choix ;
// - le pied collant n'existe que s'il porte un geste (A5).
//
// Le corps du cadre ouvert (DEV-2284, lot B ; ex-« Affiner l'étude », DEV-783).
//
// Quatre groupes, séparés par un filet : « Période et prudence » ouvert ;
// « Ce que l'étude cherche » (ex-« prendre la main »), « Mon contrat » (le
// registre servi, passé par l'emplacement `contrat`, et les corrections) et
// « Réglages avancés » sous leur en-tête porteur. Le pied, collant, dit ce que
// la relance recalcule et porte les verbes : « Relancer » (libellé par le
// nombre de modifications) et « Annuler les modifications » ; « Actualiser » a
// quitté le pied (Q26, 14/10/2026). Le brouillon vit ici, dans un
// composant qui n'est jamais démonté par un repli (v-show du cadre).
//
// Z2 — « Affiner l'étude » (pilotage, DEV-783 Lot A).
// Leviers self-service RÉELLEMENT câblés : période, marge, et corrections d'entrée
// (FTA / raccordement / dénivelé-puissance / plages HC). « Relancer » émet un objet
// d'overrides que la page passe à runBySource → le proxy api-metier les forwarde au
// moteur courtage (comptage/compteur/structure_tarifaire/optimisation). On n'envoie une
// correction QUE si elle a été modifiée (sinon le moteur résout l'entrée depuis la source).
// Conduite « prendre la main » + plancher éditable (dérogation C2) = Lot D (backend pricer).
//
// Revue du 08/10/2026 (lot F45) :
// - Q0 : le formulaire s'ouvre AVANT le premier lancement (`etude = null`). Il
//   part alors des valeurs recommandées (période du moteur, prudence
//   équilibrée, contrat et raccordement lus chez Enedis au lancement tant que
//   rien n'est saisi) ; son bouton lance la première étude. La SOURCE des
//   valeurs initiales est `getBase` : l'étude affichée, sinon un compteur dont
//   on ne connaît que le segment ;
// - R10 : l'unité du raccordement est celle du segment, elle ne se confirme
//   plus (ni sélecteur, ni `raccordement_unite_confirmee` envoyé) ;
// - R8 : un dénivelé saisi non admissible est dit sous les puissances, et
//   l'étude ne part pas tant qu'il ne l'est pas.
// ================================
import { computed, nextTick, reactive, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import OptimTurpeV2StructureFields from "./OptimTurpeV2StructureFields.vue"
import OptimTurpeV2Section from "./OptimTurpeV2Section.vue"
import {
    enteteReglagesAvances,
    piedDuCadre,
} from "@modules/courtage/utils/optim-turpe-v2.cadre"
import {
    estC5,
    estTriphase,
    formatDateFr,
    formatNombreFr,
    ftaAClasseDePointe,
    getUnitePuissance,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import { libelleFormuleTarifaireSansCode } from "@modules/courtage/utils/optim-turpe-v2.libelles"
import { lireRegistre } from "@modules/courtage/utils/optim-turpe-v2.registre"
import { lireRaccordement } from "@modules/courtage/utils/optim-turpe-v2.bornes"
import { lirePlancherRetenu } from "@modules/courtage/utils/optim-turpe-v2.recommandation"
import {
    lireFenetre,
    postesAbsentsACoupSur,
    postesAbsentsRefuses,
    MESSAGE_REFUS_POSTES,
    textesDesPostes,
} from "@modules/courtage/utils/optim-turpe-v2.fenetre"
import { amenerALEcranApresLesOuvertures } from "@modules/courtage/utils/optim-turpe-v2.defilement"
import { LOCALE_SAISIE } from "@modules/courtage/utils/optim-turpe-v2.nombre"
import {
    deniveleSaisi,
    denivelesEgaux,
    plagesDuSegment,
    refNombreSaisi,
    refusDesPlagesHoraires,
    refusDesPuissances,
    zeroAdmisSurLePoste,
    refusDuDenivele,
    segmentIncoherentAvecLaFormule,
    segmentNormalise,
    uneSeuleDate,
    uniteDuRaccordement,
} from "@modules/courtage/utils/optim-turpe-v2.saisie"
import type { AncreAffiner } from "@modules/courtage/utils/optim-turpe-v2.completer"
import {
    estMargeValide,
    fractionDepuisPourcentage,
    LIBELLE_MARGE_C5,
    libelleCranDeMarge,
    MARGE_PCT_DEFAUT,
    MARGE_PCT_MAX,
    MARGE_PCT_MIN,
    MARGE_PRESETS_PCT,
    relireValeurServie,
} from "@modules/courtage/utils/optim-turpe-v2.marge"
import type { OptimTurpeV2RunBody } from "@modules/courtage/services/optim-turpe-v2.service"
import { lireEchec } from "@modules/courtage/utils/optim-turpe-v2.echec"
import OptimTurpeV2Info from "./OptimTurpeV2Info.vue"
import OptimTurpeV2Puces from "./OptimTurpeV2Puces.vue"
import {
    NIVEAU_1,
    NIVEAU_2,
    NIVEAU_3,
} from "@modules/courtage/utils/optim-turpe-v2.boutons"
import type {
    OptimTurpeV2Etude,
    OptimTurpeV2PlageKey,
    OptimTurpeV2Segment,
} from "@modules/courtage/types/optim-turpe-v2"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            // Vague wording du 14/10/2026 (Q8, Q9, Q10, Q27, Q41, Q50 ; W72 à
            // W86 ; L1 à L5, L14) : les mots de la partie 4 du dossier des
            // arbitrages.
            groupes: {
                periode: "Période",
                // Contrat v1.2 §8.2.1 : le premier bloc, l'objectif d'abord.
                chiffrer: "Ce que vous voulez chiffrer",
                // §8.2.2 : les puissances à chiffrer, distinctes du contrat.
                cible: "Puissances à chiffrer",
                contrat: "Contrat actuel",
                // §8.2.8 et décision A6 : sous le titre du bloc, lue avant de
                // toucher une valeur.
                contratLecture:
                    "À corriger seulement si la valeur d'Enedis est fausse. Pour chiffrer d'autres puissances, choisissez « Le coût des puissances de mon choix ».",
                ciblePartielle:
                    "Renseignez la puissance de tous les postes à chiffrer.",
                // Q0 : avant le premier lancement, rien n'est encore lu.
                contratLecturePremier:
                    "Saisissez une valeur seulement si celle d'Enedis est fausse ou absente.",
                contratLuAuLancement:
                    "Formule et puissances souscrites : lues chez Enedis au lancement, tant que rien n'est saisi.",
                // Q0 : une formule sans puissances (ou l'inverse) ne se lance pas.
                contratPartiel:
                    "Saisissez la formule et toutes les puissances, ou laissez-les vides : elles seront alors lues chez Enedis au lancement.",
                // Relance : une plage effacée sur un contrat déjà chiffré.
                denivelePartiel:
                    "Renseignez la puissance de tous les postes, ou laissez-les tous vides pour garder le contrat lu chez Enedis.",
            },
            // W85 : dit ce que l'on perd.
            relancerRecommande: "Relancer sans mes modifications",
            pied: {
                annuler: "Annuler les modifications",
            },
            conduite: {
                label: "Objectif",
                optimiser: "La combinaison la moins chère",
                // L15 de Mélodie : « Le coût des puissances de mon choix ».
                prendreLaMain: "Le coût des puissances de mon choix",
                help: "La combinaison la moins chère, ou vos puissances chiffrées.",
                info: "« La combinaison la moins chère » est cherchée par l'étude. « Le coût des puissances de mon choix » chiffre la formule et les puissances que vous saisissez, telles quelles, face au contrat actuel.",
                // Revue du 08/10/2026 (R9) : « Valeurs tracées « à confirmer ». » retiré,
                // le pied du formulaire le dit une fois pour toutes.
                priceOnlyNote:
                    "Le résultat s'affichera sous « Vos puissances », face au contrat actuel.",
                // Maquette 2d (F2, F4) : d'où viennent les valeurs modifiées.
                depuisRecommandation:
                    "Préremplies avec la recommandation : {valeurs}.",
                depuisRecommandationC5:
                    "Préremplie avec la recommandation : {valeurs}.",
            },
            chiffrageSansSaisie: "Saisissez les puissances à chiffrer.",
            // Revue sceptique de L-D (D21) : les valeurs sont posées, mais ce
            // sont celles du contrat actuel.
            chiffrageIdentiqueAuContrat:
                "Saisissez des puissances différentes du contrat actuel.",
            periode: {
                label: "Période",
                // Contrat v1.2 §8.2.4.
                help: "Fenêtre de 12 mois, ajustable pour écarter une période non représentative (arrêt prolongé, hausse exceptionnelle).",
                // Q0 : avant le premier lancement, la période du moteur.
                vide: "Vide : les 12 derniers mois mesurés.",
                // L9 (23/09) : aucune borne basse au-delà de deux jours. Les
                // limites d'une fenêtre courte se disent AVANT le lancement, sur
                // le champ lui-même, et l'étude part quand même.
                // DEV-2439 : « 366 jours », « 1 jour », sans pluriel entre parenthèses.
                duree: "Période retenue : {jours} jours.",
                dureeUnJour: "Période retenue : 1 jour.",
                // Retours d'usage du 22/10/2026 : le moteur refuse aussi une
                // période d'un seul jour (début égal à la fin).
                finAvantDebut:
                    "La date de début doit précéder la date de fin : une période compte au moins deux jours. Corrigez l'une des deux dates pour relancer l'étude.",
                tropLongue:
                    "La période dépasse les {max} jours que l'étude sait analyser. Resserrez-la.",
                // D14 (revue témoin du 15/10/2026) : une seule date ne part pas.
                uneSeuleDate:
                    "Saisissez les deux dates de la période, ou videz les deux pour les 12 derniers mois mesurés.",
                // DEV-2114 et revue du 25/09 (écart 13) : sur un C4 ou un C2,
                // une période qui n'offre pas toutes les classes du dénivelé est
                // REFUSÉE. L'annonce le dit avant le clic, au lieu de promettre
                // une recommandation que le moteur ne servira pas.
                // Retours d'usage du 22/10/2026 : ce refus est CERTAIN, le
                // bouton est désactivé (`postesAbsentsACoupSur`).
                // Retours du 23/10/2026 : la phrase est partagée avec le bouton
                // qui relance sur la période proposée (`MESSAGE_REFUS_POSTES`).
                refusPostes: MESSAGE_REFUS_POSTES,
                // Cas limite (une période qui s'arrête au bord d'une saison) :
                // le refus dépend de l'heure, la page prévient sans bloquer.
                refusClasses:
                    "Cette période touche à peine la {saison} : les postes {postes} risquent de ne pas y être mesurés, et l'étude serait alors refusée. Élargissez la période pour couvrir les deux saisons.",
                // Relecture du 22/10/2026 : en « prendre la main », le moteur
                // chiffre sans refuser (calculette.py:368). La page prévient
                // en orange, sans bloquer ni dire « refusée », et ne décrit
                // pas le calcul du pricer sur ces postes (non relu).
                postesNonChiffres:
                    "Cette période ne contient aucun jour de {saison} : les postes {postes} n'y sont pas mesurés. Pour qu'ils comptent dans l'étude, choisissez dans le champ Période une période qui couvre les deux saisons, par exemple les douze derniers mois.",
                postesPeuChiffres:
                    "Cette période touche à peine la {saison} : les postes {postes} risquent de ne pas y être mesurés. Pour qu'ils comptent dans l'étude, élargissez la période pour couvrir les deux saisons.",
            },
            marge: {
                label: "Marge de sécurité",
                // Contrat v1.2 §8.2.6.
                help: "Au-dessus de la puissance retenue.",
                // Q10 : en C5, le moteur applique 15 % d'office.
                c5: "Marge de sécurité : {marge}",
                horsBorne:
                    "La marge doit être comprise entre {min} et {max} %. L'étude n'est pas relancée tant que la valeur est hors de ces bornes.",
                // L'ambiguïté se DIT sur le champ concerné, elle ne se tranche
                // pas en silence vers une autre valeur (revue C7).
                ambigue:
                    "Cette étude porte une marge de {valeur}, qui se lit {retenue} % ou {autre} %. Le formulaire affiche {retenue} % ; vérifiez avant de relancer.",
                // §8.2.6 (C24) : la valeur libre vit dans « Réglages d'expert ».
                // Revue du 19/10/2026 (N8) : le libellé dit l'effet, le champ
                // reste vide tant que le cran s'applique.
                autre: "Marge libre (%)",
                autreAide:
                    "Remplace le cran choisi au-dessus ; vide : le cran s'applique.",
                // Saisies robustes (20/10/2026) : dit sous le champ libre, qui
                // ne ramène plus la valeur à 30 % en silence. La phrase
                // « horsBorne » reste au-dessus, près des crans, pour le cas où
                // les réglages d'expert sont repliés.
                libreHorsBornes: "Saisissez une marge entre {min} et {max} %.",
                personnalisee: "{pct} % (personnalisée)",
            },
            // Revue du 19/10/2026 (N8) : des libellés qui disent l'effet, une
            // seule aide par champ, la valeur retenue dite dans l'aide.
            // Démo du 20/10 (point 2) : courtage `plancher_modulation.py`
            // (`plancher_c4_hcb`, `borne_basse_classe`) : sur un C4 le
            // plancher ne relève que le poste le plus haut, les autres restent
            // libres ; sur un C2 il relève tous les postes. Le talon relève
            // chaque poste, sur les deux segments.
            talon: {
                label: "Plancher de chaque poste ({unite})",
                help: "Aucun poste ne descend sous cette valeur. Vide : aucun plancher.",
            },
            plancher: {
                label: "Puissance la plus haute au moins ({unite})",
                help: "Les autres postes restent libres. Vide : l'étude choisit.",
                helpSegment:
                    "Les autres postes restent libres. Vide : plancher du segment, {plancher}.",
                labelTousPostes: "Plancher de tous les postes ({unite})",
                helpTousPostes:
                    "Aucun poste ne descend sous cette valeur. Vide : aucun plancher.",
                derogatoire: "Hors valeur par défaut, indiqué au rapport",
            },
            raccordement: {
                lever: "Autoriser une puissance supérieure au raccordement",
                leverHelp:
                    "Le gestionnaire de réseau devra étudier la demande : travaux et délai possibles.",
                // Revue 2 en production (I19, DEV-2404) : une seule phrase
                // visible sous la case ; ce que disait la seconde (« Dépasser
                // le raccordement demande une étude… ») vit ici, avec le prix.
                leverInfo:
                    "Par défaut, la recommandation reste sous la puissance de raccordement connue. L'autoriser au-dessus est indiqué au rapport. Si le raccordement est juste, c'est lui qu'il faut faire évoluer, pas la souscription ; le prix de cette prestation n'est pas connu de l'étude : à valider avec votre fournisseur.",
                // Q20 : la limite déjà écartée par le moteur se dit à côté de
                // la case, grisée.
                // Contrat v1.2 §8.2.10 (D19) : le texte exact sous la case grisée.
                dejaEcartee:
                    "Sans effet sur cette étude : la puissance maximale atteinte dépasse cette valeur, non retenue. Pour chiffrer des puissances qui restent en dessous : Nouvelle étude › Chiffrer d'autres puissances.",
                nonRetenue: "non retenue",
            },
            technique: {
                raccordement: "Puissance de raccordement ({unite})",
                // R10 (08/10/2026) : l'unité est celle du segment, elle ne se
                // confirme plus. Avant le premier lancement, la valeur vide
                // sera lue chez Enedis.
                raccordementLu: "Lue chez Enedis au lancement",
                // DEV-2164 cas 4 : sur un C5 triphasé, le maximum par phase
                // rouvre l'exploration de la baisse.
                pmaxParPhase: "Puissance maximale par phase (kVA)",
                pmaxParPhaseHelp:
                    "Le maximum atteint par la phase la plus chargée.",
                pmaxParPhaseInfo:
                    "Le maximum atteint par la phase la plus chargée, relevé par vous ou votre installateur. Sans lui, l'étude ne descend pas sous la puissance que l'installation tient déjà.",
                heuresCreuses: "Plages d'heures creuses",
                // DEV-2176 : les entrées que le moteur accepte en surcharge.
                heuresPointeFixe: "Plages d'heures de pointe fixe",
                noPlagePointe:
                    "Aucune plage de pointe fixe renseignée : les heures de pointe fixe par défaut d'Enedis s'appliquent.",
                // Décision A8 : le segment est en lecture seule ; un segment
                // faux se corrige côté source, pas dans l'étude.
                segment: "Segment",
                segmentLuChezEnedis: "Lu chez Enedis, non modifiable",
                segmentNonModifiable: "Non modifiable",
                segmentRefus:
                    "Ce segment ne correspond pas à la formule d'acheminement de ce compteur.",
                // D13 (revue témoin du 15/10/2026) : dit avant l'envoi.
                segmentIncoherent:
                    "Ce segment ne va pas avec la formule d'acheminement saisie : choisissez une formule de ce segment ci-dessous, ou revenez au segment du compteur.",
                branchement: "Branchement du compteur",
                monophase: "Monophasé",
                triphase: "Triphasé",
                branchementHelp:
                    "Détermine les puissances qui peuvent être souscrites.",
                branchementInfo:
                    "Le branchement détermine les puissances qui peuvent être souscrites. Corrigez-le s'il ne correspond pas à votre installation.",
                pas: "Paliers de puissance",
                pas3: "De 3 en 3 kVA",
                pas1: "De 1 en 1 kVA",
                // Contrat v1.2 §8.2.11 : le palier appliqué, avec sa provenance.
                pasApplique: "Palier appliqué : {palier}",
                addPlage: "Ajouter une plage",
                noPlage: "Aucune plage renseignée.",
                // DEV-2186 : d'où viennent les plages retenues par le calcul.
                provenance: "Source : {provenance}",
                from: "De",
                to: "À",
            },
            // Lot 4d : la provenance servie par le registre des entrées, sous
            // chaque champ du contrat (maquette 2d).
            provenance: {
                enedisF: "Lue chez Enedis",
                enedisFP: "Lues chez Enedis",
                enedisM: "Lu chez Enedis",
                saisie: "Votre saisie",
                defaut: "Valeur par défaut",
                mesure: "Mesure du compteur",
            },
        },
    },
})

// ================================
// TYPES & PROPS
// ================================
type Props = {
    /**
     * L'étude affichée (ou le refus). `null` avant le premier lancement (Q0) :
     * le formulaire part alors des valeurs recommandées.
     */
    etude: OptimTurpeV2Etude | null
    /**
     * Le segment du compteur, tel que sa source le porte : la seule chose
     * connue avant le premier lancement (Q0). Ignoré quand `etude` existe.
     */
    segmentCompteur?: string | null
    /**
     * Le champ que le client a demandé à compléter depuis un refus ou depuis le
     * registre (L29). Le panneau s'ouvre et amène le champ à l'écran ; il ne
     * remplit rien à la place du client.
     */
    ancre?: AncreAffiner | null
    /**
     * La période DEMANDÉE par le lancement qui a été refusé (DEV-2163) : les
     * réglages la gardent, au lieu de revenir à celle de l'étude d'avant.
     */
    periode?: { date_from?: string; date_to?: string } | null
    /** Ouvre le panneau d'emblée (réglages restés ouverts après un refus, DEV-2163). */
    ouvert?: boolean
    /**
     * Le refus du dernier lancement (DEV-2163), quand il n'est pas `etude`.
     * Un refus qui nomme un champ de ce panneau s'écrit À CÔTÉ du champ
     * (DEV-2176 : `segment-incoherent-avec-fta`).
     */
    refus?: OptimTurpeV2Etude | null
    /**
     * Parcours guidé, sur un refus : les seuls paramètres qui lèvent ce refus
     * (les remèdes servis). `null` : tous les paramètres.
     */
    remedes?: AncreAffiner[] | null
    /** Les ouvertures des groupes repliables du cadre, tenues par la page. */
    details?: Record<string, boolean>
    /** « Actualiser sur les données du jour » existe-t-il dans cet état ? */
    actualisable?: boolean
    /**
     * Un refus, ou une étude rouverte en échec : « Relancer l'étude » reste
     * offert sans modification et renvoie la même demande (relecture du lot B).
     */
    relancable?: boolean
    /**
     * Reco (p) : avant la première étude, les informations du contrat ne sont
     * pas reçues ; le lancement attend une formule saisie (`formule`), ou une
     * formule ou un segment saisis (`formule-ou-segment`), comme le moteur.
     */
    donneesManquantes?: "formule" | "formule-ou-segment" | null
}
type PlageRow = { from: string; to: string }
const props = withDefaults(defineProps<Props>(), {
    segmentCompteur: null,
    ancre: null,
    periode: null,
    ouvert: false,
    refus: null,
    remedes: null,
    details: () => ({}),
    actualisable: false,
    relancable: false,
    donneesManquantes: null,
})

const emit = defineEmits<{
    relancer: [overrides: OptimTurpeV2RunBody]
    /**
     * « Relancer en mode recommandé » (sur un refus) : la page relance sur les
     * paramètres recommandés, sans AUCUNE surcharge du cadre.
     */
    "relancer-recommande": []
    actualiser: []
    detail: [id: string, ouvert: boolean]
    "update:attente": [attente: { etiquette: string; note: string } | null]
}>()

const PLAGE_ORDER: OptimTurpeV2PlageKey[] = ["PTE", "HPH", "HCH", "HPB", "HCB"]

/**
 * Q0 : avant le premier lancement, le compteur dont on ne connaît que le
 * segment. Aucune valeur de contrat n'y est posée : elles seront lues chez
 * Enedis au lancement ; la prudence est la prudence par défaut.
 */
function compteurSansEtude(segment: string | null): OptimTurpeV2Etude {
    return {
        compteur: { segment: segmentNormalise(segment) ?? undefined },
    } as OptimTurpeV2Etude
}
/** Avant le premier lancement (Q0) : aucune étude n'est encore affichée. */
const getPremierLancement = computed(() => props.etude === null)
/** La SOURCE des valeurs initiales : l'étude affichée, sinon le compteur seul. */
const getBase = computed<OptimTurpeV2Etude>(
    () => props.etude ?? compteurSansEtude(props.segmentCompteur)
)

// ================================
// HELPERS — lecture de l'entrée (inputs surchargés sinon contrat actuel)
// ================================
/**
 * Les puissances chiffrées d'une étude « Vos puissances » (D12) : la cible
 * envoyée au moteur, sinon celle qu'il a tracée ; `null` sans aucune des deux.
 */
function lireCibleSaisie(etude: OptimTurpeV2Etude) {
    const cible =
        etude.inputs?.optimisation?.cible ?? etude.conduite?.cible_saisie
    return cible?.puissance_souscrite_soutirage ? cible : null
}
/**
 * Lot 4d (contrat v1.2 §8.2.8) : le bloc « Contrat actuel » part TOUJOURS du
 * contrat (la saisie corrigée, sinon le contrat lu), y compris sur une étude
 * « Vos puissances » : corriger le contrat et chiffrer des puissances sont deux
 * gestes distincts (M8, F16).
 */
function structureDeDepart(etude: OptimTurpeV2Etude) {
    return {
        fta: readStructure(etude)?.formule_tarifaire_acheminement?.fta ?? "",
        puissances:
            etude.actuelle?.structure_tarifaire?.puissance_souscrite_soutirage,
    }
}
/**
 * Lot 4d (contrat v1.2 §8.2.2, §8.2.3) : les « Puissances à chiffrer ». Une
 * étude « Vos puissances » se relit sur sa saisie (`inputs.optimisation.cible`,
 * repli `conduite.cible_saisie` ; D12) ; toute autre étude part de la
 * recommandation du compteur (`meilleure`). `depuisRecommandation` dit d'où
 * viennent les valeurs, pour la note sous les champs (maquette 2d, F2 et F4).
 */
function cibleDeDepart(etude: OptimTurpeV2Etude) {
    const saisie =
        etude.inputs?.optimisation?.mode === "price_only"
            ? lireCibleSaisie(etude)
            : null
    const recommandation = etude.meilleure?.structure_tarifaire ?? null
    const source = saisie ?? recommandation
    return {
        fta: source?.formule_tarifaire_acheminement?.fta ?? "",
        puissances: source?.puissance_souscrite_soutirage,
        depuisRecommandation:
            saisie === null &&
            Boolean(recommandation?.puissance_souscrite_soutirage),
    }
}
function readStructure(etude: OptimTurpeV2Etude) {
    return (
        etude.inputs?.structure_tarifaire ?? etude.actuelle?.structure_tarifaire
    )
}
function readPlages(etude: OptimTurpeV2Etude): PlageRow[] {
    // `inputs.comptage` ne porte que les overrides utilisateur ; à défaut on pré-remplit avec le
    // comptage RÉSOLU échoé par le moteur (`restitution.comptage_resolu`, DEV-783) — sinon le champ
    // restait « Aucune plage renseignée » au premier affichage.
    return (
        etude.inputs?.comptage?.plages_horaires?.heures_creuses ??
        etude.comptage_resolu?.plages_horaires?.heures_creuses ??
        []
    ).map((p) => ({
        from: (p.time_from ?? "").slice(0, 5),
        to: (p.time_to ?? "").slice(0, 5),
    }))
}

/** Plages de pointe fixe (C2) : surcharge, sinon le comptage résolu par le moteur. */
function readPlagesPointe(etude: OptimTurpeV2Etude): PlageRow[] {
    return (
        etude.inputs?.comptage?.plages_horaires?.heures_pointe_fixe ??
        etude.comptage_resolu?.plages_horaires?.heures_pointe_fixe ??
        []
    ).map((p) => ({
        from: (p.time_from ?? "").slice(0, 5),
        to: (p.time_to ?? "").slice(0, 5),
    }))
}

// ================================
// STATE
// ================================
/** Racine du panneau, pour amener à l'écran le champ demandé par « Compléter ». */
const racine = ref<HTMLElement | null>(null)

const dateFrom = ref<string>(
    props.periode?.date_from ?? getBase.value.inputs?.data?.date_from ?? ""
)
const dateTo = ref<string>(
    props.periode?.date_to ?? getBase.value.inputs?.data?.date_to ?? ""
)
// Le curseur porte des POINTS DE POURCENTAGE ; le moteur attend une FRACTION.
// La conversion et ses bornes vivent dans `optim-turpe-v2.marge.ts` (bloquant B1).
const margeRelue = ref(
    relireValeurServie(getBase.value.inputs?.optimisation?.marge_securite_pct)
)
const marge = ref<number>(margeRelue.value.pct)
// Conduite (DEV-787) : « laisser optimiser » (défaut) ou « prendre la main » (chiffrage sans MILP).
const mode = ref<"optimiser" | "price_only">(
    getBase.value.inputs?.optimisation?.mode === "price_only"
        ? "price_only"
        : "optimiser"
)
// Plancher de modulation (DEV-788) : pré-rempli au plancher du segment (250 C2 / 37 C4), modulable.
// A6 : la page n'annonce plus de plancher par segment (le 250 kW du C2 n'existe
// plus, la contrainte C4 est tenue par le moteur). Le champ ne porte donc que ce
// que l'utilisateur a lui-même saisi ; vide, le moteur décide.
const plancher = refNombreSaisi(
    getBase.value.inputs?.optimisation?.plancher_modulation
)
/** Puissance maximale par phase saisie sur un C5 triphasé (DEV-2164 cas 4). */
const pmaxParPhase = refNombreSaisi(
    getBase.value.inputs?.compteur?.mesures?.pmax_par_phase_kva
)
/** Talon minimum par classe (C4, C2) : opt-in, vide = aucun talon (A6, L12). */
const talon = refNombreSaisi(getBase.value.inputs?.optimisation?.talon_minimum)
/** Branchement d'un C5 (DEV-1911) : `null` tant que l'écran ne le connaît pas. */
function lireTriphase(etude: OptimTurpeV2Etude): boolean | null {
    const saisi = etude.inputs?.compteur?.raccordement?.branchement?.triphase
    if (typeof saisi === "boolean") return saisi
    const servi = etude.branchement?.valeur
    if (servi === "triphase") return true
    if (servi === "monophase") return false
    return null
}
const triphase = ref<boolean | null>(lireTriphase(getBase.value))
/**
 * Pas de 1 kVA du C5 (L24) : la saisie reprise, sinon le pas SERVI par le
 * moteur (`branchement.pas_kva`, contrat v1.2 §8.2.11 : la puce présélectionnée
 * est le palier appliqué) ; `null` quand rien n'est servi, jamais présumé.
 */
function lirePasDe1Kva(etude: OptimTurpeV2Etude): boolean | null {
    const saisi = etude.inputs?.optimisation?.pas_de_1_kva_c5
    if (typeof saisi === "boolean") return saisi
    const pas = etude.branchement?.pas_kva
    if (pas === 1) return true
    if (pas === 3) return false
    return null
}
const pasDe1Kva = ref<boolean | null>(lirePasDe1Kva(getBase.value))
/**
 * Segment tarifaire (DEV-2176) : pré-rempli de la saisie reprise, sinon du
 * segment servi. `null` tant qu'aucun n'est connu : rien n'est présumé ici.
 */
function lireSegment(etude: OptimTurpeV2Etude): OptimTurpeV2Segment | null {
    const saisi = etude.inputs?.compteur?.segment
    if (saisi === "C2" || saisi === "C4" || saisi === "C5") return saisi
    return etude.compteur?.segment ?? null
}
const segment = ref<OptimTurpeV2Segment | null>(lireSegment(getBase.value))
/** Levée de la borne de raccordement (A7) — surcharge tracée et dite au résultat. */
const raccordementLevee = ref<boolean>(
    getBase.value.inputs?.optimisation?.bornes?.raccordement_levee === true
)

// Corrections d'entrée (pré-remplies du contrat actuel ; des puissances
// chiffrées pour une étude « Vos puissances », D12).
const depart = structureDeDepart(getBase.value)
const fta = ref<string>(depart.fta)
// Effacement (prod v491) : chaque champ numérique ne garde qu'une valeur
// saisie ou `null` (`refNombreSaisi`) ; le dénivelé ne garde que ses plages
// saisies (`deniveleSaisi`, et la saisie d'une plage passe par
// `OptimTurpeV2StructureFields`). Une puissance effacée vaut ainsi « non
// saisie », comme jamais touchée.
const puissance = refNombreSaisi(depart.puissances?.puissance)
const denivele = reactive<Partial<Record<OptimTurpeV2PlageKey, number>>>(
    deniveleSaisi(depart.puissances?.denivele)
)
/**
 * La puissance de raccordement de départ : la saisie reprise, sinon la valeur
 * du compteur, sinon la valeur reçue par la borne servie (lot 4d : le champ
 * montre la valeur lue chez Enedis, même quand l'étude l'a écartée).
 */
function raccordementDeDepart(etude: OptimTurpeV2Etude): number | null {
    const borne = etude.bornes?.raccordement
    const fini = (v: unknown) =>
        typeof v === "number" && Number.isFinite(v) ? v : null
    return (
        etude.inputs?.compteur?.raccordement
            ?.puissance_raccordement_soutirage ??
        etude.compteur?.raccordement?.puissance_raccordement_soutirage ??
        fini(borne?.valeur_recue) ??
        fini(borne?.valeur)
    )
}
const raccordement = refNombreSaisi(raccordementDeDepart(getBase.value))
/** Les puissances à chiffrer (§8.2.2), distinctes du contrat. */
const departCible = cibleDeDepart(getBase.value)
const ftaCible = ref<string>(departCible.fta)
const puissanceCible = refNombreSaisi(departCible.puissances?.puissance)
const deniveleCible = reactive<Partial<Record<OptimTurpeV2PlageKey, number>>>(
    deniveleSaisi(departCible.puissances?.denivele)
)
const cibleDepuisRecommandation = ref(departCible.depuisRecommandation)
const heuresCreuses = ref<PlageRow[]>(readPlages(getBase.value))
const heuresPointeFixe = ref<PlageRow[]>(readPlagesPointe(getBase.value))

/** Snapshot des valeurs initiales — pour n'envoyer que ce qui a changé (DoD « à confirmer »). */
const initials = reactive({
    dateFrom: "",
    dateTo: "",
    marge: 0,
    mode: "optimiser" as "optimiser" | "price_only",
    plancher: null as number | null,
    talon: null as number | null,
    raccordementLevee: false,
    pasDe1Kva: null as boolean | null,
    pmaxParPhase: null as number | null,
    fta: "",
    puissance: null as number | null,
    denivele: {} as Partial<Record<OptimTurpeV2PlageKey, number>>,
    ftaCible: "",
    puissanceCible: null as number | null,
    deniveleCible: {} as Partial<Record<OptimTurpeV2PlageKey, number>>,
    raccordement: null as number | null,
    /** Le raccordement de départ était-il une SAISIE (entrées de l'étude) ? */
    raccordementSaisi: false,
    heuresCreuses: "",
    heuresPointeFixe: "",
    triphase: null as boolean | null,
    segment: null as OptimTurpeV2Segment | null,
})
function snapshot(etude: OptimTurpeV2Etude = getBase.value): void {
    // La période de référence est celle de l'étude : la période d'un refus,
    // reprise dans les champs, compte comme une modification à relancer.
    initials.dateFrom = etude.inputs?.data?.date_from ?? ""
    initials.dateTo = etude.inputs?.data?.date_to ?? ""
    initials.marge = margeRelue.value.pct
    initials.mode = mode.value
    initials.plancher = plancher.value
    initials.talon = talon.value
    initials.raccordementLevee = raccordementLevee.value
    initials.pasDe1Kva = pasDe1Kva.value
    initials.pmaxParPhase = pmaxParPhase.value
    initials.fta = fta.value
    initials.puissance = puissance.value
    initials.denivele = { ...denivele }
    initials.ftaCible = ftaCible.value
    initials.puissanceCible = puissanceCible.value
    initials.deniveleCible = { ...deniveleCible }
    initials.raccordement = raccordement.value
    initials.raccordementSaisi =
        etude.inputs?.compteur?.raccordement
            ?.puissance_raccordement_soutirage != null
    initials.heuresCreuses = JSON.stringify(heuresCreuses.value)
    initials.heuresPointeFixe = JSON.stringify(heuresPointeFixe.value)
    initials.triphase = triphase.value
    initials.segment = segment.value
}
snapshot()

// ================================
// COMPUTED
// ================================
const getUnite = computed(() => getUnitePuissance(getBase.value))
const getMargeBornes = { min: MARGE_PCT_MIN, max: MARGE_PCT_MAX }

/**
 * Contrat v1.2 §8.2.6 : les quatre crans de la marge, en `BaseSelectButton`
 * (remplace les puces et le curseur). Une marge hors cran (valeur libre de
 * « Marge libre (%) ») ne présélectionne aucun cran.
 */
const getCransMarge = MARGE_PRESETS_PCT.map((pct) => ({
    label: libelleCranDeMarge(pct) ?? `${pct} %`,
    value: pct as number,
}))
const getCranMarge = computed<number | null>({
    get: () =>
        (MARGE_PRESETS_PCT as readonly number[]).includes(marge.value)
            ? marge.value
            : null,
    set: (pct) => {
        if (typeof pct !== "number") return
        dernierCran.value = pct
        marge.value = pct
    },
})
const estUnCran = (pct: number) =>
    (MARGE_PRESETS_PCT as readonly number[]).includes(pct)
/**
 * Le dernier cran choisi aux boutons (ou relu de l'étude) : il s'applique de
 * nouveau quand la marge libre est vidée. Une saisie libre qui tombe sur un
 * cran (« 2 » en tapant « 25 ») ne le remplace pas.
 */
const dernierCran = ref<number>(
    estUnCran(marge.value) ? marge.value : MARGE_PCT_DEFAUT
)
/**
 * N8 (19/10/2026) : « Marge libre (%) » reste vide tant qu'un cran
 * s'applique (plus de « 5 » prérempli) ; une valeur saisie remplace le cran,
 * vider le champ rend le dernier cran choisi. La saisie est gardée telle
 * quelle, même quand elle égale un cran : le champ ne se vide pas sous les
 * doigts (« 2 » avant « 25 », « 5 » ou « 10 » tapés après « 7 »).
 */
const margeSaisie = ref<number | null>(
    estUnCran(marge.value) ? null : marge.value
)
/** Marge posée ailleurs (cran, relecture de l'étude) : le champ suit. */
watch(marge, (pct) => {
    if (margeSaisie.value === pct) return
    if (estUnCran(pct)) {
        margeSaisie.value = null
        dernierCran.value = pct
    } else {
        margeSaisie.value = pct
    }
})
const getMargeLibre = computed<number | null>({
    get: () => margeSaisie.value,
    set: (pct) => {
        margeSaisie.value = typeof pct === "number" ? pct : null
        marge.value = typeof pct === "number" ? pct : dernierCran.value
    },
})
/** « 7 % (personnalisée) » : une marge hors cran, dite dans les réglages d'expert. */
const getMargePersonnalisee = computed<string | null>(() =>
    getCranMarge.value === null && getMargeValide.value
        ? t("marge.personnalisee", { pct: formatNombreFr(marge.value, 1) })
        : null
)
const getPucesBranchement = computed(() => [
    {
        valeur: false,
        libelle: t("technique.monophase"),
        testid: "optim-turpe-v2-branchement-monophase",
    },
    {
        valeur: true,
        libelle: t("technique.triphase"),
        testid: "optim-turpe-v2-branchement-triphase",
    },
])
/** §8.2.11 : les deux paliers du C5, en `BaseSelectButton`. */
const getOptionsPaliers = computed(() => [
    { label: t("technique.pas3"), value: false },
    { label: t("technique.pas1"), value: true },
])
/** La marge saisie est-elle envoyable ? Sinon on le dit et on ne relance pas. */
const getMargeValide = computed(() => estMargeValide(marge.value))
/** La marge libre saisie est hors bornes : le refus se dit sous ce champ. */
const getMargeLibreHorsBornes = computed(
    () => margeSaisie.value !== null && !getMargeValide.value
)

/**
 * La valeur de marge SERVIE se lit-elle de deux façons ? On l'écrit alors sur le
 * champ, tant que l'utilisateur n'a pas lui-même déplacé le curseur — après quoi
 * la valeur affichée est la sienne et n'a plus rien d'ambigu.
 */
const getMargeAmbigue = computed<{
    valeur: string
    retenue: number
    autre: number
} | null>(() => {
    const relue = margeRelue.value
    if (
        !relue.ambigue ||
        relue.autreLecturePct === null ||
        relue.valeurServie === null ||
        marge.value !== relue.pct
    ) {
        return null
    }
    return {
        valeur: String(relue.valeurServie),
        retenue: relue.pct,
        autre: relue.autreLecturePct,
    }
})
/**
 * Unité du champ « puissance de raccordement » : celle du segment (R10, décision
 * du 08/10/2026 : C5 et C4 en kVA, C2 et C3 en kW). Le segment est celui du
 * formulaire, que le client peut corriger : la valeur saisie part sans unité, et
 * le moteur la lit dans l'unité de ce segment. Segment inconnu : l'unité que le
 * moteur a servie, sinon celle des puissances de l'étude.
 */
const getSegmentFormulaire = computed<string | null>(
    () =>
        segment.value ??
        segmentNormalise(getBase.value.compteur?.segment) ??
        getBase.value.compteur?.segment ??
        null
)
/** L'étude vue par la saisie : le segment est celui du formulaire. */
const getBaseFormulaire = computed<OptimTurpeV2Etude>(() =>
    getSegmentFormulaire.value === getBase.value.compteur?.segment
        ? getBase.value
        : ({
              ...getBase.value,
              compteur: {
                  ...getBase.value.compteur,
                  segment: getSegmentFormulaire.value,
              },
          } as OptimTurpeV2Etude)
)
const getUniteRaccordement = computed(
    () =>
        uniteDuRaccordement(getSegmentFormulaire.value) ??
        lireRaccordement(getBase.value)?.unite ??
        getUnite.value
)

/**
 * La fenêtre saisie et ses limites (L9). Lecture purement arithmétique des deux
 * dates : elle vaut avant le lancement comme après, et n'invente rien de ce que
 * le moteur servira.
 */
const getFenetre = computed(() => lireFenetre(dateFrom.value, dateTo.value))

/** Les classes du dénivelé d'un C4 ou d'un C2 ; la pointe reste optionnelle (DEV-2114). */
const CLASSES_DU_DENIVELE = ["HPH", "HCH", "HPB", "HCB"]

/**
 * Les limites annoncées sous le sélecteur. Sur un C4 ou un C2 dont la période
 * n'offre pas toutes les classes du dénivelé, le moteur REFUSE l'étude
 * (DEV-2114) : l'annonce dit ce refus et son remède, et ne garde aucune phrase
 * qui promettrait un montant ou une recommandation.
 */
const getLimitesAnnoncees = computed<string[]>(() => {
    const fenetre = getFenetre.value
    // Retours du 22/10/2026 : le segment du FORMULAIRE, normalisé (un Linky
    // P1 ou P3 est un C2 ou un C4), et non le segment brut servi.
    const segment = getSegmentDeLaGarde.value
    if (segment === "C4" || segment === "C2") {
        // Refus certain : dit en rouge par `getRefusPostes`, pas ici.
        if (getPostesAbsents.value) return []
        // En « prendre la main », aucun refus : la même absence est prévenue.
        const prendreLaMain = mode.value === "price_only"
        const certains = prendreLaMain
            ? postesAbsentsACoupSur(dateFrom.value, dateTo.value, segment)
            : null
        if (certains) {
            return [
                t(
                    "periode.postesNonChiffres",
                    textesDesPostes(certains.postes)
                ),
            ]
        }
        const absentes = fenetre.classesNonObservees.filter((classe) =>
            CLASSES_DU_DENIVELE.includes(classe)
        )
        if (absentes.length > 0) {
            return [
                t(
                    prendreLaMain
                        ? "periode.postesPeuChiffres"
                        : "periode.refusClasses",
                    textesDesPostes(absentes)
                ),
            ]
        }
    }
    return fenetre.limites
})

/**
 * Retours d'usage du 22/10/2026 : sur un C4 ou un C2, une période sans aucun
 * jour de l'une des deux saisons est REFUSÉE à coup sûr par le moteur
 * (`classes_hors_fenetre.py`). Elle ne part plus. Sauf en « prendre la main »
 * (`price_only`), où le moteur ne l'applique pas (calculette.py:368).
 */
const getPostesAbsents = computed(() =>
    postesAbsentsRefuses(
        dateFrom.value,
        dateTo.value,
        getSegmentDeLaGarde.value,
        mode.value
    )
)
/** Le segment du formulaire, ramené à C2, C4 ou C5 (un Linky P3 est un C4). */
const getSegmentDeLaGarde = computed(
    () =>
        segmentNormalise(getSegmentFormulaire.value) ??
        getSegmentFormulaire.value
)

const getRefusPostes = computed<string | null>(() =>
    getPostesAbsents.value
        ? t(
              "periode.refusPostes",
              textesDesPostes(getPostesAbsents.value.postes)
          )
        : null
)

/**
 * La raison qui empêche la période de partir, une phrase, ou `null`. Redite au
 * pied quand le champ Période est masqué (même règle que D13 et D22).
 */
const getRefusPeriode = computed<string | null>(() => {
    if (getUneSeuleDate.value) return t("periode.uneSeuleDate")
    const cause = getFenetre.value.causeInvalide
    if (cause === "fin-avant-debut") return t("periode.finAvantDebut")
    if (cause === "trop-longue") return t("periode.tropLongue", { max: 1096 })
    return getRefusPostes.value
})

/**
 * Le champ par phase n'a d'objet que sur un C5 triphasé, dit par l'étude servie
 * (`restitution.branchement`, l'entrée saisie, ou la réserve par phase).
 */
/**
 * Q20 : la limite de raccordement est déjà écartée par le moteur (la
 * puissance maximale atteinte la dépasse) ; la case « Autoriser une puissance
 * supérieure au raccordement » n'aurait aucun effet : grisée, avec le statut.
 */
const getLimiteDejaEcartee = computed(() => {
    if (raccordementLevee.value) return false
    const lu = lireRaccordement(getBase.value)
    // Lot 4d : l'écartement se lit sur le statut servi, ou sur la forme de
    // l'alerte servie (`bornes.raccordement.alerte`, courtage#311).
    const forme = getBase.value.bornes?.raccordement?.alerte?.forme
    const ecartee =
        lu?.statut === "ecartee" ||
        (typeof forme === "string" && forme.startsWith("ecartee"))
    if (!ecartee) return false
    // Une valeur ressaisie au-dessus de la puissance maximale atteinte servie
    // borne de nouveau l'étude : la case redevient active avant même la
    // relance (maquette 2d, question 7). Sous le pic, elle reste écartée
    // (écart documenté dans `decisions-v1.1.md`, D19).
    if (!getRaccordementModifie.value) return true
    const pic = lu?.pointeMesureeMax ?? null
    if (pic === null || raccordement.value === null) return true
    return raccordement.value <= pic
})

const getShowPmaxParPhase = computed(
    () => estC5(getBase.value) && (triphase.value ?? estTriphase(getBase.value))
)
const getPmaxParPhaseModifie = computed(
    () => pmaxParPhase.value !== initials.pmaxParPhase
)
/**
 * Saisies robustes (20/10/2026) : la relance d'une étude affichée est un
 * REJEU, dont les surcharges sont fusionnées par dessus les entrées héritées
 * (courtage `cycle.fusionner`). Un champ vidé qui n'envoie rien garde donc
 * l'ancienne valeur ; il part à `null`, que le moteur traite comme une absence
 * (le raccordement redevient celui lu chez Enedis).
 */
const getRaccordementRetire = computed(
    // Relecture sceptique de la PR #858 : seulement une SAISIE héritée ; une
    // valeur lue chez Enedis et vidée n'a rien à retirer (rien ne part).
    () => initials.raccordementSaisi && raccordement.value == null
)
/**
 * Un raccordement tapé à 0 (ou négatif) : le moteur le lit comme une absence
 * (`normaliser_raccordement`), la saisie disparaîtrait sans un mot.
 */
const getRefusRaccordement = computed<string | null>(() =>
    getRaccordementModifie.value &&
    typeof raccordement.value === "number" &&
    raccordement.value <= 0
        ? "La puissance de raccordement se saisit supérieure à zéro."
        : null
)
const getPmaxParPhaseRetire = computed(
    () =>
        initials.pmaxParPhase != null &&
        !(typeof pmaxParPhase.value === "number" && pmaxParPhase.value > 0)
)

/**
 * La fenêtre saisie est-elle envoyable ? Une fenêtre courte l'est TOUJOURS
 * (décision L9) : seuls l'ordre des dates et la borne haute du moteur refusent.
 */
const getFenetreEnvoyable = computed(() => {
    // Revue témoin du 15/10/2026 (D14) : une seule date n'est pas une
    // période ; elle ne part pas (elle était ignorée sans le dire).
    if (getUneSeuleDate.value) return false
    // Retours d'usage du 22/10/2026 : des postes requis absents à coup sûr.
    if (getPostesAbsents.value) return false
    const cause = getFenetre.value.causeInvalide
    return cause === null || cause === "dates-absentes"
})
const getUneSeuleDate = computed(() =>
    uneSeuleDate(dateFrom.value, dateTo.value)
)

/** Conduite « prendre la main » (DEV-787) : chiffrage d'une structure imposée, sans optimisation. */
const getIsPriceOnly = computed(() => mode.value === "price_only")

/**
 * Plancher APPLIQUÉ par le moteur, avec sa provenance (courtage#254, DEV-2185) :
 * 37 kVA en C4, plancher du segment ; rien en C2 sans saisie. Sur une étude
 * antérieure, le défaut du segment sert de repli ; l'ancien `retenu`, qui
 * portait la plus haute puissance recommandée, n'est jamais lu.
 * N8 (19/10/2026) : il est dit dans l'aide unique du champ, la valeur servie
 * par l'étude, jamais une valeur écrite en dur.
 */
const getAidePlancher = computed(() => {
    if (getPlancherSurTousLesPostes.value) return t("plancher.helpTousPostes")
    const plancher = lirePlancherRetenu(getBase.value)
    return plancher?.provenance === "segment"
        ? t("plancher.helpSegment", {
              plancher: `${formatNombreFr(plancher.valeur, 2)}\u00a0${getUnite.value}`,
          })
        : t("plancher.help")
})
/**
 * Sur un C2, le plancher saisi relève TOUS les postes (courtage
 * `borne_basse_classe`) ; sur un C4, le seul poste le plus haut.
 */
const getPlancherSurTousLesPostes = computed(
    () => getSegmentFormulaire.value === "C2"
)
/** Le nom du réglage dans les résumés, selon ce qu'il relève. */
const getNomPlancher = computed(() =>
    getPlancherSurTousLesPostes.value
        ? "plancher de tous les postes"
        : "puissance la plus haute au moins"
)
/** Le champ plancher n'a de sens que pour les segments à dénivelé (C2/C4). */
const getShowPlancher = computed(() => getBase.value.compteur?.segment !== "C5")
/** Dérogation SERVIE par le moteur, jamais déduite d'un plancher recopié ici. */
const getPlancherDerogatoire = computed(
    () => getBase.value.plancher_modulation?.derogatoire === true
)
/** Opt-in : on n'envoie le plancher que si l'utilisateur en a saisi un. */
const getPlancherModifie = computed(() => plancher.value != null)

/** Aucune puissance saisie : ni unique, ni sur une plage. */
const getPuissancesVides = computed(
    () =>
        puissance.value == null &&
        PLAGE_ORDER.every((k) => typeof denivele[k] !== "number")
)
/**
 * Les puissances ont changé. Toutes les plages effacées sans puissance unique
 * laissent le contrat lu chez Enedis tel quel : ce n'est pas une modification.
 */
const getPuissancesModifie = computed(
    () =>
        !getPuissancesVides.value &&
        (puissance.value !== initials.puissance ||
            !denivelesEgaux(denivele, initials.denivele))
)
const getStructureModifie = computed(
    () => fta.value !== initials.fta || getPuissancesModifie.value
)
const getRaccordementModifie = computed(
    () => raccordement.value !== initials.raccordement
)

/** Les plages saisies : celles de l'étude, sinon celles du segment (Q0). */
const getPlagesSaisies = computed<OptimTurpeV2PlageKey[]>(() => {
    const base = getBase.value
    // Segment corrigé dans le formulaire : les plages de l'étude ne sont plus
    // celles du segment que le client voit.
    if (
        getSegmentFormulaire.value !== segmentNormalise(base.compteur?.segment)
    ) {
        return plagesDuSegment(getSegmentFormulaire.value, fta.value)
    }
    // L17, R19 (vague wording du 14/10/2026) : seulement les postes du
    // compteur ; un C4 n'a pas de poste de pointe (QM12), même quand une
    // valeur traîne dans l'étude.
    const lues = PLAGE_ORDER.filter(
        (k) =>
            !(k === "PTE" && getSegmentFormulaire.value === "C4") &&
            (base.meilleure?.structure_tarifaire?.puissance_souscrite_soutirage
                ?.denivele?.[k] !== undefined ||
                base.actuelle?.structure_tarifaire
                    ?.puissance_souscrite_soutirage?.denivele?.[k] !==
                    undefined)
    )
    return lues.length > 0
        ? lues
        : plagesDuSegment(base.compteur?.segment, fta.value)
})

/**
 * R8 (08/10/2026) : le dénivelé SAISI est-il admissible ? Il se juge dès que le
 * client touche au contrat (ou chiffre sa propre structure), jamais sur le
 * contrat lu chez Enedis qu'il n'a pas modifié. La phrase est dite sous les
 * puissances et le lancement reste bloqué tant qu'elle s'affiche.
 */
const getRefusDenivele = computed<string | null>(() => {
    if (getEstC5.value) return null
    if (!getStructureModifie.value) return null
    if (puissance.value != null) return null
    return (
        refusDuDenivele(
            denivele,
            getSegmentFormulaire.value,
            getUnite.value,
            CHAMP_CONTRAT_EN_CLAIR
        ) ?? refusServiSur(CHAMP_DENIVELE_CONTRAT)
    )
})

/**
 * Saisies robustes (20/10/2026) : une puissance du contrat actuel à 0,
 * négative, décimale ou au-dessus de la borne du segment se refuse par une
 * phrase, avant l'envoi. Le champ ne la rogne plus (il ramenait un C2 à
 * 999 kW en silence).
 *
 * Relecture sceptique de la PR #858 : seules les puissances que le client a
 * TAPÉES (différentes de la valeur de départ) et qu'il VOIT (postes affichés)
 * se jugent. Une valeur lue chez Enedis et laissée telle quelle, ou un poste
 * caché, ne bloque jamais la relance : le client ne pourrait pas la corriger.
 */
function puissancesTapees(
    unique: number | null,
    uniqueDepart: number | null,
    postes: Partial<Record<OptimTurpeV2PlageKey, number>>,
    postesDepart: Partial<Record<OptimTurpeV2PlageKey, number>>,
    affiches: ReadonlyArray<OptimTurpeV2PlageKey>
): unknown[] {
    if (getEstC5.value) {
        return unique !== uniqueDepart ? [unique] : []
    }
    // A6 (DECISIONS-A1-A20) : en C4, un 0 sur un poste autre que le plus
    // haut est un fait du contrat, pas une faute de saisie.
    return affiches
        .filter((k) => postes[k] !== postesDepart[k])
        .filter(
            (k) =>
                !(
                    postes[k] === 0 &&
                    zeroAdmisSurLePoste(getSegmentFormulaire.value, k, affiches)
                )
        )
        .map((k) => postes[k])
}
const getRefusPuissances = computed<string | null>(() => {
    if (!getStructureModifie.value) return null
    return refusDesPuissances(
        puissancesTapees(
            puissance.value,
            initials.puissance,
            denivele,
            initials.denivele,
            getPlagesSaisies.value
        ),
        getSegmentFormulaire.value,
        getUnite.value
    )
})

// ================================
// LES PUISSANCES À CHIFFRER (lot 4d, contrat v1.2 §8.2.2)
// ================================
/** Le nom des deux champs dans un refus (courtage `CHAMP_EN_CLAIR`). */
const CHAMP_CIBLE_EN_CLAIR = "Puissances à chiffrer"
const CHAMP_CONTRAT_EN_CLAIR = "Contrat actuel › Puissances souscrites"
const CHAMP_DENIVELE_CIBLE =
    "optimisation.cible.puissance_souscrite_soutirage.denivele"
const CHAMP_DENIVELE_CONTRAT =
    "structure_tarifaire.puissance_souscrite_soutirage.denivele"

/**
 * Le refus `validation-denivele-non-admissible` servi par le moteur sur ce
 * champ (`error.details.champ`), tant que la saisie refusée n'a pas été
 * retouchée : il s'affiche sous le champ, avec le champ et les deux postes
 * nommés par le moteur.
 */
function refusServiSur(champ: string): string | null {
    const refus = props.refus
    if (!refus) return null
    const echec = lireEchec(refus)
    if (echec?.code !== "validation-denivele-non-admissible") return null
    if (refus.error?.details?.champ !== champ) return null
    return echec.message
}

/** Le chiffrage porte-t-il sur une seule puissance (C5) ? */
const getCibleVide = computed(
    () =>
        puissanceCible.value == null &&
        PLAGE_ORDER.every((k) => typeof deniveleCible[k] !== "number")
)
const getCibleModifiee = computed(
    () =>
        ftaCible.value !== initials.ftaCible ||
        puissanceCible.value !== initials.puissanceCible ||
        !denivelesEgaux(deniveleCible, initials.deniveleCible)
)
/** Les postes à chiffrer : ceux du contrat (mêmes postes, même compteur). */
const getPlagesCible = computed<OptimTurpeV2PlageKey[]>(
    () => getPlagesSaisies.value
)
/** Un poste à chiffrer laissé vide : la cible ne part pas. */
const getCiblePartielle = computed(() => {
    if (!getIsPriceOnly.value || getEstC5.value) return false
    if (getCibleVide.value) return false
    return getPlagesCible.value.some(
        (k) => typeof deniveleCible[k] !== "number"
    )
})
/** Même règle que le contrat actuel, sur les puissances à chiffrer. */
const getRefusPuissancesCible = computed<string | null>(() => {
    if (!getIsPriceOnly.value) return null
    return refusDesPuissances(
        puissancesTapees(
            puissanceCible.value,
            initials.puissanceCible,
            deniveleCible,
            initials.deniveleCible,
            getPlagesCible.value
        ),
        getSegmentFormulaire.value,
        getUnite.value
    )
})
const getRefusDeniveleCible = computed<string | null>(() => {
    if (!getIsPriceOnly.value || getEstC5.value) return null
    return (
        refusDuDenivele(
            deniveleCible,
            getSegmentFormulaire.value,
            getUnite.value,
            CHAMP_CIBLE_EN_CLAIR
        ) ?? refusServiSur(CHAMP_DENIVELE_CIBLE)
    )
})
/** « HPH 121, HCH 121, HPB 121, HCB 121 kVA » ou « 11 kVA » : la recommandation. */
const getNoteCible = computed<string | null>(() => {
    if (!cibleDepuisRecommandation.value) return null
    const unite = getUnite.value
    if (initials.puissanceCible != null) {
        return t("conduite.depuisRecommandationC5", {
            valeurs: `${formatNombreFr(initials.puissanceCible, 2)} ${unite}`,
        })
    }
    const postes = PLAGE_ORDER.filter(
        (k) => typeof initials.deniveleCible[k] === "number"
    ).map(
        (k) => `${k} ${formatNombreFr(initials.deniveleCible[k] as number, 2)}`
    )
    return postes.length > 0
        ? t("conduite.depuisRecommandation", {
              valeurs: `${postes.join(", ")} ${unite}`,
          })
        : null
})

/**
 * Relance : une plage du dénivelé effacée alors que d'autres restent saisies.
 * Le moteur n'a pas de contrat à compléter, la saisie ne part pas (même règle
 * qu'au premier lancement, message propre à la relance).
 */
const getDenivelePartiel = computed(() => {
    if (getPremierLancement.value || getEstC5.value) return false
    if (puissance.value != null) return false
    const nombres = getPlagesSaisies.value.map(
        (k) => typeof denivele[k] === "number"
    )
    return nombres.some(Boolean) && !nombres.every(Boolean)
})

/**
 * Q0 : avant le premier lancement, le contrat est lu chez Enedis tant que rien
 * n'est saisi. Une saisie partielle (une formule sans ses puissances, ou des
 * puissances sans formule) ne part pas : le moteur n'a pas de contrat à
 * compléter.
 */
const getContratPartiel = computed(() => {
    if (!getPremierLancement.value || !getStructureModifie.value) return false
    if (!fta.value) return true
    if (getEstC5.value)
        return !(typeof puissance.value === "number" && puissance.value > 0)
    return getPlagesSaisies.value.some((k) => typeof denivele[k] !== "number")
})

/**
 * Revue témoin du 15/10/2026 (D13) : le segment choisi contredit la formule
 * saisie. Le moteur le refusait après une vingtaine de secondes ; la page le
 * dit sous les puces et ne relance pas.
 */
const getSegmentIncoherent = computed(() =>
    segmentIncoherentAvecLaFormule(getSegmentFormulaire.value, fta.value)
)

/**
 * D21 : « Chiffrer d'autres puissances » sans puissance saisie, ou avec les
 * puissances et la formule du contrat actuel, chiffrerait une étude à 0 €.
 */
const getChiffrageSansSaisie = computed(() => {
    if (!getIsPriceOnly.value) return false
    if (getCibleVide.value) return true
    // Lot 4d : la cible se compare au contrat tel que le formulaire le porte
    // (corrigé ou lu), jamais à elle-même.
    if (ftaCible.value && fta.value && ftaCible.value !== fta.value)
        return false
    if (puissanceCible.value != null)
        return puissanceCible.value === puissance.value
    return denivelesEgaux(deniveleCible, denivele)
})

/** D22 : les plages d'heures creuses saisies, illisibles ou qui se chevauchent. */
const getRefusPlages = computed(() =>
    refusDesPlagesHoraires(heuresCreuses.value)
)

/** Reco (p) : la saisie qui lève le refus de la collecte manque encore. */
const getDonneesManquantes = computed(() => {
    if (props.donneesManquantes === null) return false
    if (fta.value) return false
    return props.donneesManquantes === "formule" || !segment.value
})
/** La saisie empêche-t-elle le lancement (R8, Q0, D13, D21, D22) ? */
const getSaisieBloquee = computed(
    () =>
        getRefusPuissances.value !== null ||
        getRefusPuissancesCible.value !== null ||
        getRefusRaccordement.value !== null ||
        getRefusDenivele.value !== null ||
        getContratPartiel.value ||
        getDenivelePartiel.value ||
        getSegmentIncoherent.value ||
        getChiffrageSansSaisie.value ||
        getRefusPlages.value !== null ||
        getRefusDeniveleCible.value !== null ||
        getCiblePartielle.value
)
/**
 * Les heures de pointe fixe n'existent qu'en HTA (C2), et seulement pour une
 * formule à classe de pointe (DEV-2186, même règle que le moteur) : ailleurs,
 * les proposer inviterait à corriger une valeur sans aucun effet. Une formule
 * que la page ne sait pas classer ne ferme rien.
 */
const getShowPointeFixe = computed(
    () =>
        getBase.value.compteur?.segment === "C2" &&
        ftaAClasseDePointe(
            fta.value ||
                getBase.value.actuelle?.structure_tarifaire
                    ?.formule_tarifaire_acheminement?.fta
        ) !== false
)

/**
 * « Provenance : valeur par défaut de l'étude » : la provenance de chaque
 * plage servie dans `comptage_resolu.provenance` (courtage#256, DEV-2186).
 * Rien quand elle n'est pas servie.
 */
function provenanceDesPlages(
    cle: "heures_creuses" | "heures_pointe_fixe"
): string | null {
    const servie =
        getBase.value.comptage_resolu?.provenance?.[cle] ??
        provenanceDuRegistre(`comptage.plages_horaires.${cle}`)
    return libelleDeProvenance(servie ?? null, "enedisFP")
}

/**
 * Lot 4d (maquette 2d) : la provenance d'une entrée, lue au registre servi
 * (`restitution.entrees`). Une valeur dérivée prend la source de la formule
 * (`lireRegistre`). `null` quand le registre ne la sert pas.
 */
function provenanceDuRegistre(champ: string): string | null {
    const entree = (lireRegistre(getBase.value) ?? []).find(
        (e) => e.champ === champ
    )
    return entree?.provenanceSource ?? null
}
/** « Lue chez Enedis », « Votre saisie », « Valeur par défaut »… ; `null` sinon. */
function libelleDeProvenance(
    provenance: string | null,
    accord: "enedisF" | "enedisFP" | "enedisM"
): string | null {
    if (!provenance) return null
    if (provenance === "enedis") return t(`provenance.${accord}`)
    if (provenance === "surcharge_client") return t("provenance.saisie")
    if (provenance === "defaut_moteur" || provenance === "defaut_page")
        return t("provenance.defaut")
    if (provenance === "mesure") return t("provenance.mesure")
    // Une provenance inconnue ne s'écrit pas : rien n'est présumé.
    return null
}
const getProvenanceFta = computed(() =>
    libelleDeProvenance(
        provenanceDuRegistre(
            "structure_tarifaire.formule_tarifaire_acheminement.fta"
        ),
        "enedisF"
    )
)
const getProvenancePuissances = computed(() =>
    libelleDeProvenance(
        provenanceDuRegistre(
            getEstC5.value
                ? "structure_tarifaire.puissance_souscrite_soutirage.puissance"
                : "structure_tarifaire.puissance_souscrite_soutirage.denivele"
        ),
        getEstC5.value ? "enedisF" : "enedisFP"
    )
)
const getProvenanceRaccordement = computed(() =>
    libelleDeProvenance(
        provenanceDuRegistre(
            "compteur.raccordement.puissance_raccordement_soutirage"
        ),
        "enedisF"
    )
)
/**
 * Le segment en lecture seule (décision A8) : « C4 · BT > 36 kVA, triphasé »,
 * « C5 · BT ≤ 36 kVA, monophasé », « C2 · HTA ». `null` sans segment connu.
 */
const getSegmentLu = computed<string | null>(() => {
    const seg = segmentNormalise(getBase.value.compteur?.segment)
    if (seg === "C4") return "C4 · BT > 36\u00a0kVA, triphasé"
    if (seg === "C2") return "C2 · HTA"
    if (seg === "C5") {
        const tri = lireTriphase(getBase.value)
        const branchement =
            tri === true ? ", triphasé" : tri === false ? ", monophasé" : ""
        return `C5 · BT ≤ 36\u00a0kVA${branchement}`
    }
    return getBase.value.compteur?.segment ?? null
})
const getProvenanceSegment = computed(() =>
    provenanceDuRegistre("compteur.segment") === "enedis"
        ? t("technique.segmentLuChezEnedis")
        : t("technique.segmentNonModifiable")
)
/**
 * §8.2.11 : le palier appliqué par le moteur et sa provenance servie
 * (`branchement.pas_kva`, `branchement.pas_provenance`, courtage#312), jamais
 * « usuel » ; `null` quand le pas n'est pas servi.
 */
const getPalierApplique = computed<string | null>(() => {
    const branchement = getBase.value.branchement
    const pas = branchement?.pas_kva
    if (pas !== 1 && pas !== 3) return null
    const palier = `de ${pas} en ${pas}\u00a0kVA`
    const provenance = branchement?.pas_provenance ?? null
    if (provenance === "defaut-monophase")
        return `${palier} · valeur par défaut, compteur Linky monophasé`
    if (provenance === "defaut-triphase")
        return `${palier} · valeur par défaut, triphasé`
    if (provenance === "compteur-non-communicant")
        return `${palier} · compteur non communicant`
    if (provenance === "surcharge") return `${palier} · votre saisie`
    return palier
})
const getProvenanceHeuresCreuses = computed(() =>
    provenanceDesPlages("heures_creuses")
)
const getProvenancePointeFixe = computed(() =>
    provenanceDesPlages("heures_pointe_fixe")
)
/**
 * Le talon ne s'applique jamais au C5 (A6, L12). Décision (o) du 20/10/2026
 * (contrat v1.6, 8.2.12) : en C2, un seul plancher, « Plancher de tous les
 * postes » ; « Plancher de chaque poste » n'y est pas proposé, le moteur
 * appliquant les deux de la même façon (`borne_basse_classe`). Il reste
 * visible quand l'étude porte déjà un talon, ou qu'un talon est saisi (venu
 * d'un C4 du formulaire), pour qu'on puisse le vider (envoi de `null`, L12,
 * R-T20).
 */
const getShowTalon = computed(
    () =>
        !getEstC5.value &&
        (!getPlancherSurTousLesPostes.value ||
            getBase.value.inputs?.optimisation?.talon_minimum != null ||
            talon.value != null)
)
const getEstC5 = computed(() => getSegmentFormulaire.value === "C5")
const getPointeFixeModifie = computed(
    () => JSON.stringify(heuresPointeFixe.value) !== initials.heuresPointeFixe
)
const getTriphaseModifie = computed(() => triphase.value !== initials.triphase)
/**
 * Le refus du moteur qui porte sur le segment saisi, servi tel quel à côté du
 * champ (contrat du lot courtage du 28/09 : `segment-incoherent-avec-fta`).
 */
const getRefusSegment = computed<string | null>(() => {
    const echec = lireEchec(props.refus ?? getBase.value)
    if (echec?.code !== "segment-incoherent-avec-fta") return null
    return echec.messageServi && echec.message
        ? echec.message
        : t("technique.segmentRefus")
})
const getPlagesModifie = computed(
    () => JSON.stringify(heuresCreuses.value) !== initials.heuresCreuses
)

// ================================
// LES GROUPES DU CADRE ET LE PIED (DEV-2284, lot B)
// ================================
type GroupeDuCadre = "avances"
/**
 * Les ouvertures des groupes : celles de la page quand elle les tient, une
 * copie locale sinon, pour que le cadre reste utilisable seul. Seuls les
 * réglages avancés se replient ; les autres groupes sont ouverts.
 */
const groupes = reactive<Record<GroupeDuCadre, boolean>>({
    avances: props.details.avances === true,
})
watch(
    () => props.details.avances,
    (avances) => {
        groupes.avances = avances === true
    }
)

/** Le champ est-il montré ? Tous, sauf sur un refus (les seuls remèdes). */
function voir(ancre: AncreAffiner): boolean {
    return props.remedes === null || props.remedes.includes(ancre)
}
const getVoirContrat = computed(
    () =>
        props.remedes === null ||
        (
            [
                "structure",
                "segment",
                "raccordement",
                "branchement",
                "heures-creuses",
                "pointe-fixe",
            ] as AncreAffiner[]
        ).some(voir)
)
/**
 * §8.2.1 : le bloc « Ce que vous voulez chiffrer » et son objectif, sur une
 * étude affichée (pas au premier lancement ni sur un refus).
 */
const getVoirObjectif = computed(
    () => props.remedes === null && !getPremierLancement.value
)
/**
 * §8.2.2 : les puissances à chiffrer, en mode choix ; sur un refus d'une
 * saisie chiffrée telle quelle, quand la structure est le remède.
 */
const getVoirCible = computed(
    () =>
        getIsPriceOnly.value &&
        (getVoirObjectif.value || voir("structure") || voir("chiffrer"))
)
/** §8.2.5, §8.2.6 : la marge, absente en mode choix et sur un refus. */
const getVoirMarge = computed(
    () => props.remedes === null && !getIsPriceOnly.value
)
/** §8.2.12 : les réglages d'expert, absents en mode choix. */
const getVoirExpert = computed(
    () =>
        !getIsPriceOnly.value &&
        (props.remedes === null || voir("talon") || voir("bareme"))
)
/** Un branchement C5 inconnu de l'étude se complète ; connu, il se lit. */
const getVoirBranchement = computed(
    () =>
        getEstC5.value &&
        voir("branchement") &&
        (lireTriphase(getBase.value) === null || props.ancre === "branchement")
)
/** §8.2.1 : les deux objectifs. */
const getOptionsObjectif = computed(() => [
    { label: t("conduite.optimiser"), value: "optimiser" },
    { label: t("conduite.prendreLaMain"), value: "price_only" },
])
function basculer(groupe: GroupeDuCadre, ouvert: boolean): void {
    groupes[groupe] = ouvert
    emit("detail", groupe, ouvert)
}
/** Le groupe qui porte le champ demandé par « Compléter », ou `null` (période). */
function groupeDeLAncre(ancre: AncreAffiner): GroupeDuCadre | null {
    return ancre === "talon" || ancre === "bareme" ? "avances" : null
}

/** Les réglages avancés posés dans le cadre, nommés comme dans la liste du pied. */
const getReglagesPoses = computed<string[]>(() => {
    const unite = getUnite.value
    const poses: string[] = []
    if (
        !getIsPriceOnly.value &&
        getShowPlancher.value &&
        plancher.value != null
    ) {
        poses.push(
            `${getNomPlancher.value} ${formatNombreFr(plancher.value, 2)} ${unite}`
        )
    }
    if (
        !getIsPriceOnly.value &&
        getShowTalon.value &&
        typeof talon.value === "number" &&
        talon.value > 0
    ) {
        poses.push(
            `plancher de chaque poste ${formatNombreFr(talon.value, 2)} ${unite}`
        )
    }
    if (!getIsPriceOnly.value && getMargePersonnalisee.value)
        poses.push(`marge libre ${formatNombreFr(marge.value, 1)} %`)
    return poses
})
/**
 * Contrat v1.2 §8.2.11 (maquette 2d, F3) : le résumé replié écrit ce qui est
 * posé ; en C5, le palier appliqué et sa provenance.
 */
const getEnteteAvances = computed(() =>
    enteteReglagesAvances(
        getReglagesPoses.value,
        getEstC5.value ? getPalierApplique.value : null
    )
)

/** « HPH 60 kVA, HCH 66 kVA », ou « 9 kVA » sur un C5 ; `null` sans valeur. */
function puissancesChangees(unite: string): string | null {
    if (puissance.value != null) {
        return puissance.value !== initials.puissance
            ? `${formatNombreFr(puissance.value, 2)} ${unite}`
            : null
    }
    const changees = PLAGE_ORDER.filter(
        (k) =>
            typeof denivele[k] === "number" &&
            denivele[k] !== initials.denivele[k]
    ).map((k) => `${k} ${formatNombreFr(denivele[k] as number, 2)} ${unite}`)
    return changees.length > 0 ? changees.join(", ") : null
}

/** « HPH 60, HCH 60, HPB 64, HCB 64 kVA » : l'unité une fois, à la fin (C4). */
function puissancesChangeesCourtes(unite: string): string | null {
    if (puissance.value != null) return puissancesChangees(unite)
    const changees = PLAGE_ORDER.filter(
        (k) =>
            typeof denivele[k] === "number" &&
            denivele[k] !== initials.denivele[k]
    ).map((k) => `${k} ${formatNombreFr(denivele[k] as number, 2)}`)
    return changees.length > 0 ? `${changees.join(", ")} ${unite}` : null
}

/** « HPH 60, HCH 64 kVA » ou « 12 kVA » : les valeurs changées, unité à la fin. */
function valeursChangees(
    unique: number | null,
    uniqueAvant: number | null,
    postes: Partial<Record<OptimTurpeV2PlageKey, number>>,
    postesAvant: Partial<Record<OptimTurpeV2PlageKey, number>>,
    unite: string
): string | null {
    if (unique != null) {
        return unique !== uniqueAvant
            ? `${formatNombreFr(unique, 2)} ${unite}`
            : null
    }
    const changees = PLAGE_ORDER.filter(
        (k) => typeof postes[k] === "number" && postes[k] !== postesAvant[k]
    ).map((k) => `${k} ${formatNombreFr(postes[k] as number, 2)}`)
    return changees.length > 0 ? `${changees.join(", ")} ${unite}` : null
}

function plagesLues(rows: PlageRow[]): string {
    return rows.map((r) => `${r.from} à ${r.to}`).join(", ")
}

/**
 * Ce qui change par rapport à l'étude, nommé court (maquette v0.4, pied du
 * cadre) : la liste du pied, le libellé de la relance et la mention de la
 * ligne du cadre en découlent.
 */
const getModifications = computed<{ long: string; court: string }[]>(() => {
    const unite = getUnite.value
    const liste: { long: string; court: string }[] = []
    // Revue 2 (C4, I82) : la forme courte nomme la modification pour le pied
    // collant, en une ligne ; la forme longue reste dans la note d'attente.
    const ajoute = (long: string, court: string = long) =>
        liste.push({ long, court })
    if (
        dateFrom.value !== initials.dateFrom ||
        dateTo.value !== initials.dateTo
    ) {
        const du = formatDateFr(dateFrom.value) ?? dateFrom.value
        const au = formatDateFr(dateTo.value) ?? dateTo.value
        ajoute(`période (du ${du} au ${au})`)
    }
    if (
        !getIsPriceOnly.value &&
        !getEstC5.value &&
        marge.value !== initials.marge
    ) {
        ajoute(`marge de sécurité (${formatNombreFr(marge.value, 1)} %)`)
    }
    if (mode.value !== initials.mode) {
        // Revue témoin du 15/10/2026 (D21, Q27) : plus de « telle(s) quelle(s) ».
        ajoute(
            getIsPriceOnly.value
                ? "objectif : le coût des puissances de mon choix"
                : "retour à la combinaison la moins chère",
            "objectif"
        )
    }
    // Lot 4d (§8.2.2) : les puissances à chiffrer, nommées avec leurs valeurs
    // (maquette 2d, F2 : « puissances (HPH 60, HCH 64 kVA) »).
    if (getIsPriceOnly.value && getCibleModifiee.value) {
        if (ftaCible.value !== initials.ftaCible && ftaCible.value) {
            ajoute(
                `formule à chiffrer (${libelleFormuleTarifaireSansCode(ftaCible.value)})`,
                "formule à chiffrer"
            )
        }
        const valeurs = valeursChangees(
            puissanceCible.value,
            initials.puissanceCible,
            deniveleCible,
            initials.deniveleCible,
            unite
        )
        if (valeurs) {
            const nom = getEstC5.value ? "puissance" : "puissances"
            ajoute(`${nom} à chiffrer (${valeurs})`, `${nom} (${valeurs})`)
        }
    }
    if (fta.value !== initials.fta) {
        // Q2 : la formule en clair, sans code.
        ajoute(
            fta.value ? libelleFormuleTarifaireSansCode(fta.value) : "formule"
        )
    }
    if (getPuissancesModifie.value) {
        // D21 : les puissances changées, nommées avec leur valeur.
        const valeurs = puissancesChangees(unite)
        const nom = getEstC5.value
            ? "puissance souscrite"
            : "puissances souscrites"
        // Lot 4d : en mode choix, « puissances » nomme les puissances à
        // chiffrer ; la correction du contrat se dit « contrat actuel ».
        const court = getIsPriceOnly.value
            ? "contrat actuel"
            : getEstC5.value
              ? "puissance"
              : "puissances"
        const valeursCourtes = puissancesChangeesCourtes(unite)
        ajoute(
            valeurs ? `${nom} (${valeurs})` : nom,
            valeursCourtes ? `${court} (${valeursCourtes})` : court
        )
    }
    if (getEstC5.value && getTriphaseModifie.value && triphase.value !== null) {
        ajoute(`branchement (${triphase.value ? "triphasé" : "monophasé"})`)
    }
    if (getRaccordementModifie.value && raccordement.value != null) {
        ajoute(
            `raccordement (${formatNombreFr(raccordement.value, 2)} ${getUniteRaccordement.value})`
        )
    } else if (getRaccordementRetire.value) {
        ajoute("raccordement saisi retiré")
    }
    if (getShowPmaxParPhase.value && getPmaxParPhaseModifie.value) {
        ajoute(
            getPmaxParPhaseRetire.value
                ? "maximum par phase retiré"
                : "maximum par phase"
        )
    }
    if (getPlagesModifie.value) {
        ajoute(
            heuresCreuses.value.length > 0
                ? `heures creuses (${plagesLues(heuresCreuses.value)})`
                : "heures creuses"
        )
    }
    if (getShowPointeFixe.value && getPointeFixeModifie.value) {
        ajoute("heures de pointe")
    }
    if (plancher.value !== initials.plancher && getShowPlancher.value) {
        ajoute(
            plancher.value != null
                ? `${getNomPlancher.value} (${formatNombreFr(plancher.value, 2)} ${unite})`
                : getPlancherSurTousLesPostes.value
                  ? "plancher de tous les postes retiré"
                  : "réglage de la puissance la plus haute retiré"
        )
    }
    if (talon.value !== initials.talon && getShowTalon.value) {
        ajoute(
            typeof talon.value === "number" && talon.value > 0
                ? `plancher de chaque poste (${formatNombreFr(talon.value, 2)} ${unite})`
                : "plancher de chaque poste retiré"
        )
    }
    if (
        !getIsPriceOnly.value &&
        raccordementLevee.value !== initials.raccordementLevee
    ) {
        ajoute(
            raccordementLevee.value
                ? "puissance supérieure au raccordement autorisée"
                : "puissance limitée au raccordement"
        )
    }
    if (
        !getIsPriceOnly.value &&
        getEstC5.value &&
        pasDe1Kva.value !== initials.pasDe1Kva
    ) {
        ajoute(
            pasDe1Kva.value ? "paliers de 1 en 1 kVA" : "paliers de 3 en 3 kVA"
        )
    }
    return liste
})

const getPied = computed(() => {
    const pied = piedDuCadre({
        modifications: getModifications.value.map((m) => m.long),
        modificationsCourtes: getModifications.value.map((m) => m.court),
        actualisable: props.actualisable,
        relancable: props.relancable,
        premierLancement: getPremierLancement.value,
    })
    // Parcours guidé, sur un refus : « Relancer en mode recommandé » repart des
    // paramètres recommandés et abandonne la modification qui a provoqué le
    // refus (constat du 07/10 : la période refusée repartait, en boucle). Les
    // remèdes saisis partent par « Relancer avec N modifications », inchangé ;
    // sans modification, le bouton recommandé remplace « Relancer l'étude »
    // tant que seuls les remèdes sont montrés.
    if (!props.relancable && props.remedes === null) {
        return { ...pied, recommande: null }
    }
    const sansModification = getModifications.value.length === 0
    return {
        ...pied,
        relancer:
            sansModification && props.remedes !== null ? null : pied.relancer,
        recommande: t("relancerRecommande"),
    }
})
watch(
    () => getPied.value.attente,
    (attente) => emit("update:attente", attente),
    { immediate: true }
)

/** Le pied porte-t-il un geste ? Sinon il ne colle pas (2.3 point 8). */
/**
 * La marge droite du pied collant : la place du bouton « Aide » de
 * l'application (fixe, `right-8`, environ 6 rem de large), plus un jour
 * (démo du 20/10, point 4).
 */
const PIED_RESERVE_AIDE = "pr-[8.5rem]"
const getPiedABouton = computed(() =>
    Boolean(
        getPied.value.relancer ||
            getPied.value.recommande ||
            getPied.value.annuler
    )
)

/** « Annuler les modifications » : le cadre repart de l'étude affichée. */
function onAnnuler(): void {
    charger(getBase.value, false)
}

/**
 * « Relancer en mode recommandé » : le cadre quitte la saisie refusée (il
 * repart de l'étude affichée, le pied dit « Aucune modification ») et la page
 * relance sans surcharge. L'étude recommandée, à son retour, recharge le cadre
 * sur ses propres valeurs.
 */
function onRelancerRecommande(): void {
    charger(getBase.value, false)
    emit("relancer-recommande")
}

// ================================
// WATCHERS — re-cale quand l'étude change (re-run / changement de compteur)
// ================================
/** Remet chaque champ sur l'étude (nouvelle étude, ou « Annuler les modifications »). */
function charger(etude: OptimTurpeV2Etude, avecPeriodeDemandee = true): void {
    dateFrom.value =
        (avecPeriodeDemandee ? props.periode?.date_from : null) ??
        etude.inputs?.data?.date_from ??
        ""
    dateTo.value =
        (avecPeriodeDemandee ? props.periode?.date_to : null) ??
        etude.inputs?.data?.date_to ??
        ""
    margeRelue.value = relireValeurServie(
        etude.inputs?.optimisation?.marge_securite_pct
    )
    marge.value = margeRelue.value.pct
    mode.value =
        etude.inputs?.optimisation?.mode === "price_only"
            ? "price_only"
            : "optimiser"
    plancher.value = etude.inputs?.optimisation?.plancher_modulation ?? null
    raccordementLevee.value =
        etude.inputs?.optimisation?.bornes?.raccordement_levee === true
    const { fta: ftaDeDepart, puissances } = structureDeDepart(etude)
    fta.value = ftaDeDepart
    puissance.value = puissances?.puissance ?? null
    for (const key of PLAGE_ORDER) delete denivele[key]
    Object.assign(denivele, deniveleSaisi(puissances?.denivele))
    const cible = cibleDeDepart(etude)
    ftaCible.value = cible.fta
    puissanceCible.value = cible.puissances?.puissance ?? null
    for (const key of PLAGE_ORDER) delete deniveleCible[key]
    Object.assign(deniveleCible, deniveleSaisi(cible.puissances?.denivele))
    cibleDepuisRecommandation.value = cible.depuisRecommandation
    raccordement.value = raccordementDeDepart(etude)
    heuresCreuses.value = readPlages(etude)
    heuresPointeFixe.value = readPlagesPointe(etude)
    talon.value = etude.inputs?.optimisation?.talon_minimum ?? null
    triphase.value = lireTriphase(etude)
    segment.value = lireSegment(etude)
    pasDe1Kva.value = lirePasDe1Kva(etude)
    pmaxParPhase.value =
        etude.inputs?.compteur?.mesures?.pmax_par_phase_kva ?? null
    snapshot(etude)
}
// Q0 : avant le premier lancement, passer d'un compteur C4 à un C5 change la
// base (le segment) ; le formulaire repart de ce compteur.
watch(getBase, (etude) => charger(etude))

/** Période demandée et ouverture imposées par la page après un refus (DEV-2163). */
watch(
    () => [props.periode?.date_from, props.periode?.date_to] as const,
    ([from, to]) => {
        if (from) dateFrom.value = from
        if (to) dateTo.value = to
    }
)

/**
 * Le champ qui reçoit le focus. Revue 2 en production (I80, DEV-2404) :
 * « Chiffrer d'autres puissances » amène la PREMIÈRE PUISSANCE (HPH en C4, la
 * puissance unique en C5), pas la formule qui la précède dans le bloc.
 */
function champACibler(
    cible: HTMLElement | null | undefined,
    puissance: boolean
): HTMLElement | null {
    if (!cible) return null
    if (puissance) {
        const champ = cible.querySelector<HTMLElement>(
            '[data-testid^="optim-turpe-v2-cible-denivele-"], [data-testid="optim-turpe-v2-cible-puissance"]'
        )
        const saisie = champ?.matches("input")
            ? champ
            : champ?.querySelector<HTMLElement>("input")
        if (saisie) return saisie
    }
    return cible.querySelector<HTMLElement>("input, select, textarea")
}

/**
 * « Compléter » (L29) : le panneau s'ouvre et amène le champ demandé à l'écran.
 *
 * On observe l'ancre à chaque changement, y compris à l'identique côté page
 * (celle-ci remet `null` entre deux demandes), pour qu'un deuxième clic sur le
 * même levier rouvre bien le panneau.
 */
watch(
    () => props.ancre,
    async (ancreDemandee) => {
        if (!ancreDemandee) return
        // Q27 et lot 4d (§8.1.2) : « Chiffrer d'autres puissances » ouvre le
        // bloc « Ce que vous voulez chiffrer » réglé sur « Le coût des
        // puissances de mon choix », le curseur dans la première puissance à
        // chiffrer.
        if (ancreDemandee === "chiffrer") mode.value = "price_only"
        const ancre: AncreAffiner = ancreDemandee
        // Le champ peut vivre dans un groupe replié : le groupe s'ouvre d'abord.
        const groupe = groupeDeLAncre(ancre)
        if (groupe) basculer(groupe, true)
        await nextTick()
        const cible = racine.value?.querySelector<HTMLElement>(
            `[data-ancre="${ancre}"]`
        )
        // Le défilement est un confort : son absence (environnement de test,
        // navigateur ancien) ne doit pas empêcher l'ouverture du panneau.
        if (typeof cible?.scrollIntoView === "function") {
            cible.scrollIntoView({ block: "center" })
        }
        const champ = champACibler(cible, ancreDemandee === "chiffrer")
        champ?.focus()
        // A8 (rejeu du 20/10) : la rangée qui porte le formulaire s'ouvre en
        // s'animant ; le défilement fait pendant l'animation se perd. Le champ
        // est recentré une fois les ouvertures finies, s'il n'est pas visible.
        void amenerALEcranApresLesOuvertures(champ)
    },
    { immediate: true }
)

// ================================
// INTERACTIONS
// ================================
function onAddPlage(): void {
    // D22 : une plage ajoutée part vide, jamais en double de 22:00 à 06:00.
    heuresCreuses.value.push({ from: "", to: "" })
}
function onRemovePlage(index: number): void {
    heuresCreuses.value.splice(index, 1)
}
function onAddPlagePointe(): void {
    heuresPointeFixe.value.push({ from: "09:00", to: "11:00" })
}
function onRemovePlagePointe(index: number): void {
    heuresPointeFixe.value.splice(index, 1)
}
function versPlages(rows: PlageRow[]) {
    return rows.map((r) => ({
        time_from: `${r.from}:00`,
        time_to: `${r.to}:00`,
    }))
}

/**
 * Structure saisie (FTA + puissance unique OU dénivelé) : le contrat corrigé
 * (`structure_tarifaire`) ou les puissances à chiffrer (`optimisation.cible`).
 */
function buildStructure(
    ftaSaisie: string,
    unique: number | null,
    postes: Partial<Record<OptimTurpeV2PlageKey, number>>
): NonNullable<OptimTurpeV2RunBody["structure_tarifaire"]> {
    const structure: NonNullable<OptimTurpeV2RunBody["structure_tarifaire"]> = {
        formule_tarifaire_acheminement: { fta: ftaSaisie },
    }
    // Plages toutes vides : rien n'est envoyé, le contrat lu est gardé.
    if (unique != null) {
        structure.puissance_souscrite_soutirage = { puissance: unique }
    } else if (PLAGE_ORDER.some((k) => typeof postes[k] === "number")) {
        structure.puissance_souscrite_soutirage = { denivele: { ...postes } }
    }
    return structure
}

/** Assemble l'objet d'overrides — période/raccordement/plages communs ; le reste dépend de la conduite. */
function onRelancer(): void {
    // Refus AVANT l'envoi (constat F2.2) : une marge hors borne ne part pas. On
    // n'envoie pas 0 à la place, ce qui affirmerait « aucune marge » sur une
    // saisie qu'on n'a pas comprise.
    if (!getIsPriceOnly.value && !getMargeValide.value) return
    // L9 : une fenêtre COURTE part quand même, avec ses limites dites au-dessus.
    // Seuls des dates dans le désordre ou au-delà de la borne haute du moteur
    // sont retenus ici, pour éviter un aller-retour inutile.
    if (!getFenetreEnvoyable.value) return
    // R8 et Q0 : un dénivelé non admissible, ou un contrat saisi à moitié avant
    // le premier lancement, ne part pas ; la raison est dite sous le champ.
    if (getSaisieBloquee.value) return

    const overrides: OptimTurpeV2RunBody = { optimisation: {} }

    if (getIsPriceOnly.value) {
        // « Prendre la main » (DEV-787) : la structure saisie est la CIBLE chiffrée telle quelle (sans
        // MILP). On NE touche PAS à structure_tarifaire → l'« actuelle » reste le contrat source courant.
        overrides.optimisation = {
            mode: "price_only",
            cible: buildStructure(
                ftaCible.value,
                puissanceCible.value,
                deniveleCible
            ),
        }
        // Lot 4d : le contrat corrigé part à côté de la cible (deux champs
        // distincts, relayés tels quels par api-metier).
        if (getStructureModifie.value) {
            overrides.structure_tarifaire = buildStructure(
                fta.value,
                puissance.value,
                denivele
            )
        }
    } else {
        // « Laisser optimiser » : marge + plancher (opt-in) + corrections d'entrée si modifiées.
        // FRACTION, jamais le pourcentage du curseur (bloquant B1).
        const fraction = fractionDepuisPourcentage(marge.value)
        overrides.optimisation =
            fraction === null ? {} : { marge_securite_pct: fraction }
        if (initials.mode === "price_only") {
            // Saisies robustes (20/10/2026) : retour à « La combinaison la
            // moins chère » depuis une étude « Vos puissances ». Le rejeu
            // hérite de `mode` et de `cible` ; sans `null` explicite, il
            // rechiffrait la même structure imposée.
            overrides.optimisation.mode = null
            overrides.optimisation.cible = null
        }
        if (getPlancherModifie.value && plancher.value != null) {
            overrides.optimisation.plancher_modulation = plancher.value
        } else if (initials.plancher != null && getShowPlancher.value) {
            // Démo du 20/10 (point 1) : la relance d'une étude affichée est un
            // REJEU, dont les surcharges sont fusionnées par dessus les entrées
            // héritées (courtage `cycle.fusionner`). Un champ vidé qui
            // n'envoie rien garde donc l'ancienne valeur ; `null` la retire.
            overrides.optimisation.plancher_modulation = null
        }
        if (getStructureModifie.value) {
            overrides.structure_tarifaire = buildStructure(
                fta.value,
                puissance.value,
                denivele
            )
        }
    }

    if (dateFrom.value && dateTo.value) {
        overrides.date_from = dateFrom.value
        overrides.date_to = dateTo.value
    }
    if (getRaccordementModifie.value && raccordement.value != null) {
        overrides.compteur = {
            raccordement: {
                puissance_raccordement_soutirage: raccordement.value,
            },
        }
    } else if (getRaccordementRetire.value) {
        overrides.compteur = {
            raccordement: { puissance_raccordement_soutirage: null },
        }
    }
    // R10 (08/10/2026) : l'unité du raccordement est celle du segment ; le
    // front n'envoie plus `raccordement_unite_confirmee` (le champ reste
    // accepté par l'API, il n'est simplement plus posé).
    // DEV-2164 cas 4 : le maximum par phase, sur un C5 triphasé seulement,
    // tracé « à confirmer » par le moteur comme toute surcharge.
    if (
        getShowPmaxParPhase.value &&
        typeof pmaxParPhase.value === "number" &&
        pmaxParPhase.value > 0
    ) {
        overrides.compteur = {
            ...overrides.compteur,
            mesures: { pmax_par_phase_kva: pmaxParPhase.value },
        }
    } else if (getShowPmaxParPhase.value && getPmaxParPhaseRetire.value) {
        overrides.compteur = {
            ...overrides.compteur,
            mesures: { pmax_par_phase_kva: null },
        }
    }
    // A7 : la levée de la borne est une surcharge comme une autre — envoyée, tracée
    // par le moteur, dite au résultat. On ne l'envoie que si elle est demandée.
    if (!getIsPriceOnly.value && raccordementLevee.value) {
        overrides.optimisation = {
            ...overrides.optimisation,
            bornes: { raccordement_levee: true },
        }
    } else if (!getIsPriceOnly.value && initials.raccordementLevee) {
        // Case décochée sur une étude qui portait la levée : le rejeu
        // l'hériterait. `false`, et non `null` : le modèle du moteur attend un
        // booléen (`InputsBornesModel.raccordement_levee`).
        overrides.optimisation = {
            ...overrides.optimisation,
            bornes: { raccordement_levee: false },
        }
    }
    // DEV-2176 : les plages sont une surcharge que le moteur accepte (liste
    // blanche de `resolve_inputs_and_load`), envoyée seulement quand l'une des
    // deux listes est modifiée, et tracée « correction du client » au registre.
    // DEV-2311 (constat du 07/10) : le moteur exige les DEUX listes ensemble
    // (`PlagesHorairesModel`), sans quoi il répond 422. La liste non modifiée
    // part telle que lue de l'étude, et `[]` quand le compteur n'en a pas.
    if (
        getPlagesModifie.value ||
        (getShowPointeFixe.value && getPointeFixeModifie.value)
    ) {
        overrides.comptage = {
            plages_horaires: {
                heures_creuses: versPlages(heuresCreuses.value),
                heures_pointe_fixe: versPlages(heuresPointeFixe.value),
            },
        }
    }
    if (
        !getIsPriceOnly.value &&
        getShowTalon.value &&
        typeof talon.value === "number" &&
        talon.value > 0
    ) {
        overrides.optimisation = {
            ...overrides.optimisation,
            talon_minimum: talon.value,
        }
    } else if (
        !getIsPriceOnly.value &&
        getShowTalon.value &&
        initials.talon != null
    ) {
        // Même règle que le plancher : un talon vidé part à `null`.
        overrides.optimisation = {
            ...overrides.optimisation,
            talon_minimum: null,
        }
    }
    if (estC5(getBase.value)) {
        if (triphase.value !== null && getTriphaseModifie.value) {
            overrides.compteur = {
                ...overrides.compteur,
                raccordement: {
                    ...overrides.compteur?.raccordement,
                    branchement: { triphase: triphase.value },
                },
            }
        }
        // §8.2.11 : la puce présélectionnée est le palier servi ; elle ne part
        // en surcharge que si elle l'était déjà ou si elle a changé.
        if (
            pasDe1Kva.value !== null &&
            !getIsPriceOnly.value &&
            (typeof getBase.value.inputs?.optimisation?.pas_de_1_kva_c5 ===
                "boolean" ||
                pasDe1Kva.value !== initials.pasDe1Kva)
        ) {
            overrides.optimisation = {
                ...overrides.optimisation,
                pas_de_1_kva_c5: pasDe1Kva.value,
            }
        }
    }
    emit("relancer", overrides)
}
</script>

<template>
    <!-- ================================ -->
    <!-- « Modifier les hypothèses » (contrat v1.2 §8.2, maquette 2d, lot 4d) :
         « Ce que vous voulez chiffrer » (objectif, puissances à chiffrer,
         période, marge), « Contrat actuel », « Réglages d'expert
         (facultatif) » repliés, puis le pied, présent dès la première
         modification seulement (A5). Sur un refus, seuls les paramètres qui
         lèvent ce refus sont montrés (`remedes`). -->
    <!-- ================================ -->
    <div
        ref="racine"
        class="optim-turpe-v2-affiner flex min-w-0 flex-col"
        data-testid="optim-turpe-v2-affiner"
    >
        <!-- 1. Ce que vous voulez chiffrer (§8.2.1 à §8.2.6). -->
        <section
            v-if="getVoirObjectif || voir('periode') || getVoirMarge"
            class="flex min-w-0 flex-col gap-4 pb-4"
            data-testid="optim-turpe-v2-groupe-chiffrer"
        >
            <h3
                v-if="getVoirObjectif"
                class="text-sm font-semibold text-primary-900"
            >
                {{ t("groupes.chiffrer") }}
            </h3>
            <div
                v-if="getVoirObjectif"
                data-testid="optim-turpe-v2-cherche"
                class="flex min-w-0 flex-col gap-1.5"
            >
                <span
                    id="optim-turpe-v2-objectif"
                    class="text-sm font-medium text-primary-700"
                    >{{ t("conduite.label") }}
                    <OptimTurpeV2Info
                        :texte="t('conduite.info')"
                        testid="optim-turpe-v2-info-conduite"
                /></span>
                <BaseSelectButton
                    v-model="mode"
                    :options="getOptionsObjectif"
                    option-label="label"
                    option-value="value"
                    :allow-empty="false"
                    size="small"
                    aria-labelledby="optim-turpe-v2-objectif"
                    class="flex-wrap"
                    data-testid="optim-turpe-v2-objectif"
                />
                <p
                    class="text-xs text-primary-500"
                    data-testid="optim-turpe-v2-aide"
                >
                    {{ t("conduite.help") }}
                </p>
            </div>

            <!-- §8.2.2 : les puissances à chiffrer, seconde instance des champs
                 de structure, distinctes du contrat. -->
            <div
                v-if="getVoirCible"
                data-ancre="chiffrer"
                class="flex min-w-0 scroll-mt-24 flex-col gap-3 rounded-md border border-surface-200 bg-surface-50 p-4"
                data-testid="optim-turpe-v2-cible"
            >
                <h4 class="text-sm font-semibold text-primary-700">
                    {{ t("groupes.cible") }}
                </h4>
                <OptimTurpeV2StructureFields
                    :etude="getBaseFormulaire"
                    :plages="getPlagesCible"
                    prefixe="optim-turpe-v2-cible"
                    :note-puissances="getNoteCible"
                    v-model:fta="ftaCible"
                    v-model:puissance="puissanceCible"
                    v-model:denivele="deniveleCible"
                />
                <p
                    v-if="getRefusPuissancesCible"
                    class="text-xs text-[var(--p-red-700)]"
                    role="alert"
                    data-testid="optim-turpe-v2-cible-puissances-refus"
                >
                    {{ getRefusPuissancesCible }}
                </p>
                <p
                    v-else-if="getRefusDeniveleCible"
                    class="text-xs text-[var(--p-red-700)]"
                    role="alert"
                    data-testid="optim-turpe-v2-cible-refus"
                >
                    {{ getRefusDeniveleCible }}
                </p>
                <p
                    v-else-if="getCiblePartielle"
                    class="text-xs text-[var(--p-red-700)]"
                    role="alert"
                    data-testid="optim-turpe-v2-cible-partielle"
                >
                    {{ t("groupes.ciblePartielle") }}
                </p>
                <!-- D21 : chiffrer sans saisie, ou le contrat tel quel,
                     donnerait une étude à 0 €. -->
                <p
                    v-else-if="getChiffrageSansSaisie"
                    class="text-xs text-[var(--p-red-700)]"
                    data-testid="optim-turpe-v2-chiffrage-sans-saisie"
                >
                    {{
                        getCibleVide
                            ? t("chiffrageSansSaisie")
                            : t("chiffrageIdentiqueAuContrat")
                    }}
                </p>
                <p
                    class="text-xs text-primary-500"
                    data-testid="optim-turpe-v2-aide"
                >
                    {{ t("conduite.priceOnlyNote") }}
                </p>
            </div>

            <!-- §8.2.4 : la période. -->
            <div
                v-if="voir('periode')"
                data-ancre="periode"
                class="flex flex-col gap-1.5"
                data-testid="optim-turpe-v2-groupe-periode"
            >
                <p class="text-sm font-medium text-primary-700">
                    {{ t("periode.label") }}
                </p>
                <div class="flex flex-wrap items-center gap-2">
                    <BaseInputText
                        v-model="dateFrom"
                        type="date"
                        class="w-40 max-w-full"
                        :aria-label="t('technique.from')"
                    />
                    <span class="text-primary-400">→</span>
                    <BaseInputText
                        v-model="dateTo"
                        type="date"
                        class="w-40 max-w-full"
                        :aria-label="t('technique.to')"
                    />
                </div>
                <!-- L9 : aucune borne basse. Ce qui est refusé, c'est
                     l'ordre des dates et la borne haute du moteur. -->
                <p
                    v-if="getUneSeuleDate"
                    class="text-xs text-[var(--p-red-600)]"
                    data-testid="optim-turpe-v2-periode-une-date"
                >
                    {{ t("periode.uneSeuleDate") }}
                </p>
                <p
                    v-else-if="getFenetre.causeInvalide === 'fin-avant-debut'"
                    class="text-xs text-[var(--p-red-600)]"
                    data-testid="optim-turpe-v2-periode-ordre"
                >
                    {{ t("periode.finAvantDebut") }}
                </p>
                <p
                    v-else-if="getFenetre.causeInvalide === 'trop-longue'"
                    class="text-xs text-[var(--p-red-600)]"
                >
                    {{ t("periode.tropLongue", { max: 1096 }) }}
                </p>
                <p
                    v-else-if="getRefusPostes"
                    class="text-xs text-[var(--p-red-600)]"
                    data-testid="optim-turpe-v2-periode-refus-postes"
                >
                    {{ getRefusPostes }}
                </p>
                <!-- Les LIMITES de la fenêtre choisie, dites avant le clic,
                     dans la donnée et sans bannière (L9). -->
                <template v-else-if="getLimitesAnnoncees.length">
                    <p
                        v-for="limite in getLimitesAnnoncees"
                        :key="limite"
                        class="text-xs text-[var(--p-orange-700)]"
                        data-testid="optim-turpe-v2-fenetre-limite"
                    >
                        {{ limite }}
                    </p>
                </template>
                <p
                    v-else-if="!dateFrom && !dateTo"
                    class="text-xs text-primary-500"
                    data-testid="optim-turpe-v2-periode-vide"
                >
                    {{ t("periode.vide") }}
                </p>
                <p
                    v-else
                    class="text-xs text-primary-500"
                    data-testid="optim-turpe-v2-aide"
                >
                    {{ t("periode.help") }}
                    <template v-if="getFenetre.jours !== null">
                        {{
                            getFenetre.jours === 1
                                ? t("periode.dureeUnJour")
                                : t("periode.duree", {
                                      jours: getFenetre.jours,
                                  })
                        }}
                    </template>
                </p>
            </div>

            <!-- §8.2.5 et §8.2.6 : la marge de sécurité ; en C5, 15 % d'office
                 (Q10), aucun contrôle. -->
            <p
                v-if="getVoirMarge && getEstC5"
                class="text-sm font-medium text-primary-700"
                data-testid="optim-turpe-v2-marge-c5"
            >
                {{ t("marge.c5", { marge: LIBELLE_MARGE_C5 }) }}
            </p>
            <div
                v-else-if="getVoirMarge"
                class="flex flex-col gap-1.5"
                data-testid="optim-turpe-v2-groupe-marge"
            >
                <span
                    id="optim-turpe-v2-marge"
                    class="text-sm font-medium text-primary-700"
                    data-testid="optim-turpe-v2-marge-ligne"
                    >{{ t("marge.label") }}</span
                >
                <BaseSelectButton
                    v-model="getCranMarge"
                    :options="getCransMarge"
                    option-label="label"
                    option-value="value"
                    :allow-empty="false"
                    size="small"
                    aria-labelledby="optim-turpe-v2-marge"
                    class="flex-wrap"
                    data-testid="optim-turpe-v2-marge-crans"
                />
                <!-- Hors borne : le fait est dit SUR le champ concerné, et la
                     relance est refusée avant l'envoi. Pas de bannière. -->
                <p
                    v-if="!getMargeValide"
                    class="text-xs text-[var(--p-red-600)]"
                >
                    {{ t("marge.horsBorne", getMargeBornes) }}
                </p>
                <p
                    v-else-if="getMargeAmbigue"
                    class="text-xs text-[var(--p-orange-700)]"
                    data-testid="marge-ambigue"
                >
                    {{ t("marge.ambigue", getMargeAmbigue) }}
                </p>
                <p
                    v-else-if="getMargePersonnalisee"
                    class="text-xs text-primary-500"
                    data-testid="optim-turpe-v2-marge-personnalisee"
                >
                    {{ getMargePersonnalisee }}
                </p>
                <p
                    v-else
                    class="text-xs text-primary-500"
                    data-testid="optim-turpe-v2-aide"
                >
                    {{ t("marge.help") }}
                </p>
            </div>
        </section>

        <!-- 2. Le contrat actuel (§8.2.8 à §8.2.10). -->
        <section
            v-if="getVoirContrat"
            class="flex min-w-0 flex-col gap-4 border-t border-surface-200 py-4"
            data-testid="optim-turpe-v2-groupe-contrat"
        >
            <div class="flex flex-col gap-1">
                <h3 class="text-sm font-semibold text-primary-900">
                    {{ t("groupes.contrat") }}
                </h3>
                <!-- A6 : la consigne sous le titre, lue avant de toucher une
                     valeur. -->
                <p
                    v-if="remedes === null"
                    class="text-xs text-primary-500"
                    data-testid="optim-turpe-v2-contrat-consigne"
                >
                    {{
                        getPremierLancement
                            ? t("groupes.contratLecturePremier")
                            : t("groupes.contratLecture")
                    }}
                </p>
            </div>
            <div
                v-if="voir('structure')"
                data-ancre="structure"
                class="flex flex-col gap-3"
            >
                <p
                    v-if="getPremierLancement && !getStructureModifie"
                    class="text-xs text-primary-500"
                    data-testid="optim-turpe-v2-contrat-lu-au-lancement"
                >
                    {{ t("groupes.contratLuAuLancement") }}
                </p>
                <OptimTurpeV2StructureFields
                    :etude="getBaseFormulaire"
                    :plages="getPlagesSaisies"
                    :provenance-fta="getProvenanceFta"
                    :provenance-puissances="getProvenancePuissances"
                    v-model:fta="fta"
                    v-model:puissance="puissance"
                    v-model:denivele="denivele"
                />
                <!-- R8 : un dénivelé non admissible se dit ici, tout de suite,
                     et l'étude ne part pas tant qu'il ne l'est pas. -->
                <p
                    v-if="getRefusPuissances"
                    class="text-xs text-[var(--p-red-600)]"
                    role="alert"
                    data-testid="optim-turpe-v2-puissances-refus"
                >
                    {{ getRefusPuissances }}
                </p>
                <p
                    v-else-if="getRefusDenivele"
                    class="text-xs text-[var(--p-red-600)]"
                    role="alert"
                    data-testid="optim-turpe-v2-denivele-refus"
                >
                    {{ getRefusDenivele }}
                </p>
                <p
                    v-else-if="getContratPartiel"
                    class="text-xs text-[var(--p-red-600)]"
                    role="alert"
                    data-testid="optim-turpe-v2-contrat-partiel"
                >
                    {{ t("groupes.contratPartiel") }}
                </p>
                <p
                    v-else-if="getDenivelePartiel"
                    class="text-xs text-[var(--p-red-600)]"
                    role="alert"
                    data-testid="optim-turpe-v2-denivele-partiel"
                >
                    {{ t("groupes.denivelePartiel") }}
                </p>
            </div>

            <!-- A8 : le segment en lecture seule, avec sa provenance. -->
            <div
                v-if="getSegmentLu && voir('segment')"
                data-ancre="segment"
                data-testid="optim-turpe-v2-segment"
                class="flex flex-col gap-1"
            >
                <span class="text-sm font-medium text-primary-700">{{
                    t("technique.segment")
                }}</span>
                <p
                    class="text-sm text-primary-900"
                    data-testid="optim-turpe-v2-segment-lu"
                >
                    {{ getSegmentLu }}
                </p>
                <p
                    v-if="getRefusSegment"
                    class="text-xs text-[var(--p-red-700)]"
                    data-testid="optim-turpe-v2-segment-refus"
                >
                    {{ getRefusSegment }}
                </p>
                <p
                    v-else-if="getSegmentIncoherent"
                    class="text-xs text-[var(--p-red-700)]"
                    data-testid="optim-turpe-v2-segment-incoherent"
                >
                    {{ t("technique.segmentIncoherent") }}
                </p>
                <p
                    class="text-xs text-primary-500"
                    data-testid="optim-turpe-v2-provenance-segment"
                >
                    {{ getProvenanceSegment }}
                </p>
            </div>

            <!-- Un branchement C5 que l'étude ne connaît pas se complète ici ;
                 connu, il se lit dans la ligne du segment. -->
            <div
                v-if="getVoirBranchement"
                data-ancre="branchement"
                data-testid="optim-turpe-v2-branchement"
                class="flex flex-col gap-1"
            >
                <div class="flex flex-wrap items-center gap-2">
                    <span class="text-sm font-medium text-primary-700">{{
                        t("technique.branchement")
                    }}</span>
                    <OptimTurpeV2Puces
                        v-model="triphase"
                        :puces="getPucesBranchement"
                        :libelle="t('technique.branchement')"
                    />
                </div>
                <p
                    class="text-xs text-primary-500"
                    data-testid="optim-turpe-v2-aide"
                >
                    {{ t("technique.branchementHelp") }}
                    <OptimTurpeV2Info
                        :texte="t('technique.branchementInfo')"
                        testid="optim-turpe-v2-info-branchement"
                    />
                </p>
            </div>

            <!-- §8.2.10 (D19) : la puissance de raccordement et la case de
                 levée ; valeur écartée : « non retenue », case grisée et le
                 texte du contrat. En mode choix, la case est sans effet :
                 masquée. -->
            <div
                v-if="voir('raccordement')"
                data-ancre="raccordement"
                class="flex flex-col gap-1"
            >
                <label
                    for="optim-turpe-v2-raccordement-saisie"
                    class="text-sm font-medium text-primary-700"
                    >{{
                        t("technique.raccordement", {
                            unite: getUniteRaccordement,
                        })
                    }}</label
                >
                <BaseInputNumber
                    :locale="LOCALE_SAISIE"
                    v-model="raccordement"
                    input-id="optim-turpe-v2-raccordement-saisie"
                    :min="0"
                    :suffix="` ${getUniteRaccordement}`"
                    :placeholder="
                        getPremierLancement
                            ? t('technique.raccordementLu')
                            : undefined
                    "
                    class="w-full sm:w-60"
                    input-class="!text-primary-900 font-medium"
                    data-testid="optim-turpe-v2-raccordement"
                />
                <p
                    v-if="getRefusRaccordement"
                    class="text-xs text-[var(--p-red-600)]"
                    role="alert"
                    data-testid="optim-turpe-v2-raccordement-refus"
                >
                    {{ getRefusRaccordement }}
                </p>
                <p
                    v-if="getProvenanceRaccordement || getLimiteDejaEcartee"
                    class="flex flex-wrap items-center gap-1 text-xs text-primary-500"
                    data-testid="optim-turpe-v2-provenance-raccordement"
                >
                    <span v-if="getProvenanceRaccordement">{{
                        getProvenanceRaccordement
                    }}</span>
                    <span
                        v-if="getProvenanceRaccordement && getLimiteDejaEcartee"
                        aria-hidden="true"
                        >·</span
                    >
                    <BaseTag
                        v-if="getLimiteDejaEcartee"
                        severity="secondary"
                        :value="t('raccordement.nonRetenue')"
                        data-testid="optim-turpe-v2-raccordement-non-retenue"
                    />
                </p>
                <div
                    v-if="!getIsPriceOnly"
                    data-ancre="limite"
                    class="flex flex-col gap-1 pt-1"
                >
                    <!-- A7 : la borne se lève, la levée est tracée par le moteur
                         et dite au résultat (réserve `raccordement-borne-levee`). -->
                    <label
                        class="flex items-start gap-2 text-sm"
                        :class="
                            getLimiteDejaEcartee
                                ? 'text-primary-400'
                                : 'text-primary-800'
                        "
                    >
                        <BaseCheckbox
                            v-model="raccordementLevee"
                            binary
                            class="mt-0.5"
                            :disabled="getLimiteDejaEcartee"
                        />
                        <span>{{ t("raccordement.lever") }}</span>
                    </label>
                    <p
                        v-if="getLimiteDejaEcartee"
                        class="text-xs text-primary-500"
                        data-testid="optim-turpe-v2-limite-deja-ecartee"
                    >
                        {{ t("raccordement.dejaEcartee") }}
                    </p>
                    <p
                        v-else
                        class="text-xs text-primary-500"
                        data-testid="optim-turpe-v2-aide"
                    >
                        {{ t("raccordement.leverHelp") }}
                        <OptimTurpeV2Info
                            :texte="t('raccordement.leverInfo')"
                            testid="optim-turpe-v2-info-limite"
                        />
                    </p>
                </div>
            </div>

            <div
                v-if="getShowPmaxParPhase && voir('raccordement')"
                data-ancre="pmax-par-phase"
                data-testid="optim-turpe-v2-pmax-par-phase"
                class="flex flex-col gap-1"
            >
                <label class="text-sm font-medium text-primary-700">{{
                    t("technique.pmaxParPhase")
                }}</label>
                <BaseInputNumber
                    :locale="LOCALE_SAISIE"
                    v-model="pmaxParPhase"
                    :min="0"
                    :max="36"
                    :max-fraction-digits="2"
                    suffix=" kVA"
                    class="w-full sm:w-60"
                    input-class="!text-primary-900 font-medium"
                />
                <p
                    class="text-xs text-primary-500"
                    data-testid="optim-turpe-v2-aide"
                >
                    {{ t("technique.pmaxParPhaseHelp") }}
                    <OptimTurpeV2Info
                        :texte="t('technique.pmaxParPhaseInfo')"
                        testid="optim-turpe-v2-info-pmax"
                    />
                </p>
            </div>

            <!-- A8 : les plages d'heures creuses, modifiables, avec leur
                 provenance servie (`comptage_resolu.provenance`, sinon le
                 registre des entrées). -->
            <div
                v-if="voir('heures-creuses')"
                data-ancre="heures-creuses"
                class="flex flex-col gap-2"
            >
                <span class="text-sm font-medium text-primary-700">{{
                    t("technique.heuresCreuses")
                }}</span>
                <p
                    v-if="heuresCreuses.length === 0"
                    class="text-xs text-primary-500"
                >
                    {{ t("technique.noPlage") }}
                </p>
                <div
                    v-for="(row, index) in heuresCreuses"
                    :key="`hc-${index}`"
                    class="flex flex-wrap items-end gap-3"
                >
                    <label class="flex flex-col gap-1 text-xs text-primary-600"
                        >{{ t("technique.from") }}
                        <BaseInputText
                            v-model="row.from"
                            type="time"
                            placeholder="HH:MM"
                            class="w-28"
                            data-testid="optim-turpe-v2-plage-debut"
                        />
                    </label>
                    <label class="flex flex-col gap-1 text-xs text-primary-600"
                        >{{ t("technique.to") }}
                        <BaseInputText
                            v-model="row.to"
                            type="time"
                            placeholder="HH:MM"
                            class="w-28"
                            data-testid="optim-turpe-v2-plage-fin"
                        />
                    </label>
                    <BaseButton
                        icon="pi pi-times"
                        size="small"
                        text
                        severity="secondary"
                        aria-label="Retirer la plage"
                        @click="onRemovePlage(index)"
                    />
                </div>
                <p
                    v-if="getRefusPlages"
                    class="text-xs text-[var(--p-red-700)]"
                    data-testid="optim-turpe-v2-plages-refus"
                >
                    {{ getRefusPlages }}
                </p>
                <p
                    v-if="getProvenanceHeuresCreuses"
                    class="text-xs text-primary-500"
                    data-testid="optim-turpe-v2-provenance-heures_creuses"
                >
                    {{ getProvenanceHeuresCreuses }}
                </p>
                <BaseButton
                    :label="t('technique.addPlage')"
                    icon="pi pi-plus"
                    size="small"
                    text
                    severity="secondary"
                    class="w-fit"
                    @click="onAddPlage"
                />
            </div>

            <div
                v-if="
                    getShowPointeFixe &&
                    (voir('pointe-fixe') || voir('heures-creuses'))
                "
                data-ancre="pointe-fixe"
                data-testid="optim-turpe-v2-pointe-fixe"
                class="flex flex-col gap-2"
            >
                <label class="text-sm font-medium text-primary-700">{{
                    t("technique.heuresPointeFixe")
                }}</label>
                <p
                    v-if="getProvenancePointeFixe"
                    class="text-xs text-primary-500"
                    data-testid="optim-turpe-v2-provenance-heures_pointe_fixe"
                >
                    {{ getProvenancePointeFixe }}
                </p>
                <p
                    v-if="heuresPointeFixe.length === 0"
                    class="text-xs text-primary-500"
                >
                    {{ t("technique.noPlagePointe") }}
                </p>
                <div
                    v-for="(row, index) in heuresPointeFixe"
                    :key="`pf-${index}`"
                    class="flex flex-wrap items-center gap-2"
                >
                    <span class="text-xs text-primary-600">{{
                        t("technique.from")
                    }}</span>
                    <BaseInputText
                        v-model="row.from"
                        placeholder="HH:MM"
                        class="w-24"
                    />
                    <span class="text-xs text-primary-600">{{
                        t("technique.to")
                    }}</span>
                    <BaseInputText
                        v-model="row.to"
                        placeholder="HH:MM"
                        class="w-24"
                    />
                    <BaseButton
                        icon="pi pi-times"
                        size="small"
                        text
                        severity="secondary"
                        aria-label="Retirer la plage"
                        @click="onRemovePlagePointe(index)"
                    />
                </div>
                <BaseButton
                    :label="t('technique.addPlage')"
                    icon="pi pi-plus"
                    size="small"
                    text
                    severity="secondary"
                    class="w-fit"
                    data-testid="optim-turpe-v2-pointe-fixe-ajouter"
                    @click="onAddPlagePointe"
                />
            </div>
        </section>

        <!-- 3. Réglages d'expert (facultatif), repliés (§8.2.11, §8.2.12) ;
             absents en mode choix (F15 : jamais annoncés « sans effet »). -->
        <div
            v-if="getVoirExpert"
            class="py-4"
            data-testid="optim-turpe-v2-reglages-expert"
        >
            <OptimTurpeV2Section
                id="acc-avances"
                :entete="getEnteteAvances"
                :rang="2"
                :ouvert="groupes.avances"
                @update:ouvert="basculer('avances', $event)"
            >
                <div class="flex min-w-0 flex-col gap-4 pb-3">
                    <!-- §8.2.11 : le palier appliqué en C5, présélectionné. -->
                    <div
                        v-if="getEstC5"
                        data-testid="optim-turpe-v2-pas-1-kva"
                        class="flex flex-col gap-1.5"
                    >
                        <span
                            id="optim-turpe-v2-paliers"
                            class="text-sm font-medium text-primary-700"
                            >{{ t("technique.pas") }}</span
                        >
                        <BaseSelectButton
                            v-model="pasDe1Kva"
                            :options="getOptionsPaliers"
                            option-label="label"
                            option-value="value"
                            :allow-empty="false"
                            size="small"
                            aria-labelledby="optim-turpe-v2-paliers"
                            class="flex-wrap"
                            data-testid="optim-turpe-v2-paliers"
                        />
                        <p
                            v-if="getPalierApplique"
                            class="text-xs text-primary-500"
                            data-testid="optim-turpe-v2-palier-applique"
                        >
                            {{
                                t("technique.pasApplique", {
                                    palier: getPalierApplique,
                                })
                            }}
                        </p>
                    </div>

                    <!-- §8.2.6 (C24) : la valeur libre de la marge. -->
                    <section
                        v-if="!getEstC5"
                        data-ancre="autre-marge"
                        class="flex flex-col gap-1.5"
                        data-testid="optim-turpe-v2-autre-marge"
                    >
                        <label
                            for="optim-turpe-v2-autre-marge-saisie"
                            class="text-sm font-medium text-primary-700"
                            >{{ t("marge.autre") }}</label
                        >
                        <BaseInputNumber
                            :locale="LOCALE_SAISIE"
                            v-model="getMargeLibre"
                            input-id="optim-turpe-v2-autre-marge-saisie"
                            :max-fraction-digits="1"
                            class="w-full sm:w-40"
                            input-class="!text-primary-900 font-medium"
                            data-testid="optim-turpe-v2-marge-libre"
                        />
                        <!-- Saisies robustes (20/10/2026) : le champ n'a plus
                             de bornes, qui ramenaient 100 ou 150 % à 30 % sans
                             un mot ; la valeur hors bornes se refuse ici. -->
                        <p
                            v-if="getMargeLibreHorsBornes"
                            class="text-xs text-[var(--p-red-600)]"
                            role="alert"
                            data-testid="optim-turpe-v2-marge-libre-refus"
                        >
                            {{ t("marge.libreHorsBornes", getMargeBornes) }}
                        </p>
                        <p
                            class="text-xs text-primary-500"
                            data-testid="optim-turpe-v2-aide"
                        >
                            {{ t("marge.autreAide") }}
                        </p>
                    </section>

                    <!-- Plancher de modulation (DEV-788) : C2/C4. -->
                    <section
                        v-if="getShowPlancher"
                        data-ancre="plancher"
                        class="flex flex-col gap-1.5"
                    >
                        <div class="flex items-center justify-between">
                            <label
                                for="optim-turpe-v2-plancher-saisie"
                                class="text-sm font-medium text-primary-700"
                                >{{
                                    t(
                                        getPlancherSurTousLesPostes
                                            ? "plancher.labelTousPostes"
                                            : "plancher.label",
                                        { unite: getUnite }
                                    )
                                }}</label
                            >
                            <BaseTag
                                v-if="getPlancherDerogatoire"
                                severity="warn"
                                :value="t('plancher.derogatoire')"
                            />
                        </div>
                        <BaseInputNumber
                            :locale="LOCALE_SAISIE"
                            v-model="plancher"
                            input-id="optim-turpe-v2-plancher-saisie"
                            :min="0"
                            :suffix="` ${getUnite}`"
                            class="w-full sm:w-60"
                            input-class="!text-primary-900 font-medium"
                            data-testid="optim-turpe-v2-plancher"
                        />
                        <p
                            class="text-xs text-primary-500"
                            data-testid="optim-turpe-v2-aide"
                        >
                            {{ getAidePlancher }}
                        </p>
                    </section>

                    <!-- Talon minimum (L12, DEV-2176) : C2/C4. -->
                    <section
                        v-if="getShowTalon"
                        data-ancre="talon"
                        class="flex flex-col gap-1.5"
                    >
                        <label
                            for="optim-turpe-v2-talon-saisie"
                            class="text-sm font-medium text-primary-700"
                            >{{ t("talon.label", { unite: getUnite }) }}</label
                        >
                        <BaseInputNumber
                            :locale="LOCALE_SAISIE"
                            v-model="talon"
                            input-id="optim-turpe-v2-talon-saisie"
                            :min="0"
                            :suffix="` ${getUnite}`"
                            class="w-full sm:w-60"
                            input-class="!text-primary-900 font-medium"
                            data-testid="optim-turpe-v2-talon"
                        />
                        <p
                            class="text-xs text-primary-500"
                            data-testid="optim-turpe-v2-aide"
                        >
                            {{ t("talon.help") }}
                        </p>
                    </section>

                    <!-- Q41 (14/10/2026) : la date du barème n'est plus un champ
                         (une seule grille depuis le 01/08/2026) ; la grille
                         appliquée se lit dans les hypothèses de l'étude. -->
                </div>
            </OptimTurpeV2Section>
        </div>

        <!-- Le pied (§8.2.13, décision A5) : présent seulement dès la première
             modification (ou sur un refus relançable, ou avant le premier
             lancement), collant pour rester visible à 560 px ; aucun bouton
             inactif au repos. -->
        <!-- Démo du 20/10 (point 4) : le bouton « Aide » de l'application
             (`SupportButton`, fixe à 2 rem du bas et de la droite, une
             centaine de pixels de large) recouvrait « Annuler les
             modifications » ; le pied lui réserve sa place à droite
             (`PIED_RESERVE_AIDE`), sans toucher au bouton. -->
        <div
            v-if="getPiedABouton"
            :class="PIED_RESERVE_AIDE"
            class="sticky bottom-0 z-[4] -mx-5 -mb-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-surface-200 bg-white py-3 pl-5 shadow-[0_-6px_10px_-8px_rgba(0,0,0,0.18)]"
            data-testid="optim-turpe-v2-cadre-pied"
        >
            <!-- I82 : une seule ligne de résumé, le texte entier au survol. -->
            <p
                class="min-w-0 flex-[1_1_20rem] truncate text-sm text-primary-800"
                :title="getPied.liste"
                data-testid="optim-turpe-v2-cadre-liste"
            >
                {{ getPied.liste }}
            </p>
            <!-- Revue sceptique de L-D (D13, D22) : sur un refus, le bloc qui
                 porte la raison peut être masqué ; la raison du blocage reste
                 dite au pied. -->
            <p
                v-if="getSegmentIncoherent && !voir('segment')"
                class="basis-full text-xs text-[var(--p-red-700)]"
                data-testid="optim-turpe-v2-pied-segment-incoherent"
            >
                {{ t("technique.segmentIncoherent") }}
            </p>
            <p
                v-if="getRefusPeriode && !voir('periode')"
                class="basis-full text-xs text-[var(--p-red-700)]"
                data-testid="optim-turpe-v2-pied-periode-refus"
            >
                {{ getRefusPeriode }}
            </p>
            <p
                v-if="getRefusPlages && !voir('heures-creuses')"
                class="basis-full text-xs text-[var(--p-red-700)]"
                data-testid="optim-turpe-v2-pied-plages-refus"
            >
                {{ getRefusPlages }}
            </p>
            <div class="flex flex-wrap items-center gap-4">
                <BaseButton
                    v-if="getPied.relancer"
                    v-bind="NIVEAU_1"
                    :label="getPied.relancer"
                    icon="pi pi-refresh"
                    class="w-fit"
                    :disabled="
                        (!getIsPriceOnly && !getMargeValide) ||
                        !getFenetreEnvoyable ||
                        getSaisieBloquee ||
                        getDonneesManquantes
                    "
                    data-testid="optim-turpe-v2-cadre-relancer"
                    @click="onRelancer"
                />
                <BaseButton
                    v-if="getPied.recommande"
                    v-bind="getPied.relancer ? NIVEAU_2 : NIVEAU_1"
                    :label="getPied.recommande"
                    icon="pi pi-refresh"
                    class="w-fit"
                    data-testid="optim-turpe-v2-cadre-relancer-recommande"
                    @click="onRelancerRecommande"
                />
                <BaseButton
                    v-if="getPied.annuler"
                    v-bind="NIVEAU_3"
                    :label="t('pied.annuler')"
                    data-testid="optim-turpe-v2-cadre-annuler"
                    @click="onAnnuler"
                />
            </div>
        </div>
    </div>
</template>
